import { useEffect, useRef, useState, useCallback } from 'react'
import { useOutletContext } from 'react-router-dom'
import { api, auth, isConversationLost, lostConvMessage, LOST_MESSAGES } from '../lib/api.js'
import { onChange } from '../lib/changes.js'
import { SELECTABLE_OUTCOMES, outcomeLabel, isSuccess, requiresPaymentVerification } from '../lib/outcomes.js'
import { paymentLabel, displayOutcome, resultPlaceholder } from '../lib/orderPayment.js'
import { receptionLabel } from '../lib/conversationReception.js'
import { ReplyModal } from './Settings.jsx'
import { MediaBubble, KbMediaBubble, findSentMedia, ImageLightbox, fmtDur, DocumentCard } from '../components/Media.jsx'
import { TEXT_LIMIT, textLen, isAgentTextAllowed } from '../lib/textLimit.js'
import { confirmBox, alertBox } from '../components/Dialog.jsx'
import { useBackAction, useMobile } from '../platform/mobile.js'
import { serverUrl } from '../platform/server.js'
import { OrderSyncStatus, PaymentCheckModal, LegacyOrderStatus } from '../components/OrderSync.jsx'
import ContentSelect from '../components/ContentSelect.jsx'
import DateRangePicker from '../components/DateRangePicker.jsx'
import DispatchNotice from '../components/DispatchNotice.jsx'
import OperatorStats from '../components/OperatorStats.jsx'
import { DOCUMENT_ACCEPT, documentType, documentDownloadName } from '../lib/documentUpload.js'
import { beijingDateString, formatBeijingTime } from '../lib/time.js'

const COL_W_MIN = 220, COL_W_MAX = 520, COL_W_BASE = 320
// 列宽按比例换算字号缩放：拉宽一点字变大一点，拉窄一点字变小一点，320px 时是 1 倍
const colZoom = w => Math.min(1.3, Math.max(0.8, w / COL_W_BASE))

function useColWidth(key, fallback) {
  const [w, setW] = useState(() => {
    try { return Number(localStorage.getItem(key)) || fallback } catch { return fallback }
  })
  const set = v => { setW(v); try { localStorage.setItem(key, String(v)) } catch {} }
  return [w, set]
}

// 拖拽分界线：side='left' 时往右拖变宽，side='right' 时往右拖变窄（分界线在栏的右侧 vs 左侧）
function ColResizer({ width, setWidth, side }) {
  const dragRef = useRef(null)
  const onDown = e => {
    e.preventDefault()
    dragRef.current = { startX: e.clientX, startW: width }
    const onMove = ev => {
      const { startX, startW } = dragRef.current
      const delta = ev.clientX - startX
      const next = startW + (side === 'left' ? delta : -delta)
      setWidth(Math.min(COL_W_MAX, Math.max(COL_W_MIN, Math.round(next))))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }
  return <div className="wb-resizer" onMouseDown={onDown} />
}

const TABS = [
  { key: 'ai', label: 'AI接待' },
  { key: 'waiting_human', label: '待接入' },
  { key: 'human', label: '人工接管' },
  { key: 'closed', label: '已关闭' },
]

const STATUS_TAG = {
  ai: ['tag-ai', 'AI接待中'],
  waiting_human: ['tag-wait', '等待接入'],
  human: ['tag-human', '人工接管'],
  closed: ['tag-closed', '已关闭'],
}

const OUTCOME_FILTERS = [
  { key: '', label: '全部结果' },
  { key: 'success_any', label: '全部成交' },
  ...SELECTABLE_OUTCOMES,
]

// 表情头像：直接用 Gavin 给的三张图（frontend/public/faces/），不重画。
// 成交用 face-success（笑脸+金冠），失败用 face-failed（哭脸+闪电），跟进中/未标记用 face-neutral。
// AI/人工在成交图额头位置叠一个小字：AI 写「A」，人工写「人」。
function FaceAvatar({ outcome, source, unread }) {
  const success = isSuccess(outcome)
  const failed = outcome === 'failed'
  const img = success ? 'face-success' : failed ? 'face-failed' : 'face-neutral'
  const letter = success && (source === 'ai' ? 'A' : source === 'human' ? '人' : null)
  return (
    <div style={{ position: 'relative', flexShrink: 0, width: 60, height: 60 }}>
      <img className="face-avatar-img" src={`${import.meta.env.BASE_URL}faces/${img}.png`} alt="" width={60} height={60}
        style={{ display: 'block', objectFit: 'contain' }} />
      {letter && (
        <span style={{ position: 'absolute', left: '50%', top: '34%', transform: 'translate(-50%,-50%)', fontSize: 8, fontWeight: 700, color: 'var(--text)' }}>{letter}</span>
      )}
      {/* 有客户没看过的新消息：头像右上角红底数字角标，超过99显示99+（2026-10-06 Gavin 要求改成数字，红点不明显） */}
      {unread > 0 && (
        <span title={`${unread} 条未读`} style={{
          position: 'absolute', top: -4, right: -4, minWidth: 18, height: 18, padding: '0 4px', borderRadius: 9,
          background: 'var(--danger)', color: '#fff', border: '2px solid var(--card)',
          fontSize: 11, fontWeight: 700, lineHeight: '14px', textAlign: 'center',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{unread > 99 ? '99+' : unread}</span>
      )}
    </div>
  )
}

const fmtTime = (t) => formatBeijingTime(t, { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })

// 触发红线的会话在列表里标红并置顶，运营打开工作台第一眼就知道该介入哪一单
const RISK_LABEL = {
  complaint: '投诉退款', abuse: '情绪激烈', aftersale: '售后问题', refund: '反复要退款', deposit_refund: '退押金',
}

// 企业微信/微信经典表情包：插入 [名称] 这种文本标记，客户端（企微/微信）自己渲染成动态表情，
// 不用传图片。这里的 emoji 只是选择器里给运营自己看的近似图标，发出去的是方括号文字（2026-10-06 Gavin 要）
const WECOM_EMOJIS = [
  ['微笑', '😊'], ['撇嘴', '😏'], ['色', '😍'], ['发呆', '😳'], ['得意', '😎'], ['流泪', '😢'],
  ['害羞', '☺️'], ['闭嘴', '🤐'], ['睡', '😴'], ['大哭', '😭'], ['尴尬', '😅'], ['发怒', '😠'],
  ['调皮', '😜'], ['呲牙', '😁'], ['惊讶', '😲'], ['难过', '😔'], ['酷', '😎'], ['冷汗', '😓'],
  ['抓狂', '😫'], ['吐', '🤮'], ['偷笑', '🤭'], ['愉快', '😄'], ['白眼', '🙄'], ['傲慢', '😤'],
  ['饥饿', '🤤'], ['困', '😪'], ['惊恐', '😱'], ['流汗', '😅'], ['憨笑', '😄'], ['悠闲', '😌'],
  ['奋斗', '💪'], ['咒骂', '🤬'], ['疑问', '❓'], ['嘘', '🤫'], ['晕', '😵'], ['折磨', '😩'],
  ['衰', '😑'], ['骷髅', '💀'], ['敲打', '🔨'], ['再见', '👋'], ['擦汗', '😅'], ['抠鼻', '🤏'],
  ['鼓掌', '👏'], ['糗大了', '😳'], ['坏笑', '😏'], ['左哼哼', '😤'], ['右哼哼', '😤'], ['哈欠', '🥱'],
  ['鄙视', '😒'], ['委屈', '🥺'], ['快哭了', '🥺'], ['阴险', '😈'], ['亲亲', '😘'], ['吓', '😨'],
  ['可怜', '🥹'], ['菜刀', '🔪'], ['西瓜', '🍉'], ['啤酒', '🍺'], ['篮球', '🏀'], ['乒乓', '🏓'],
  ['咖啡', '☕'], ['饭', '🍚'], ['猪头', '🐷'], ['玫瑰', '🌹'], ['凋谢', '🥀'], ['示爱', '💞'],
  ['爱心', '❤️'], ['心碎', '💔'], ['蛋糕', '🎂'], ['闪电', '⚡'], ['炸弹', '💣'], ['刀', '🔪'],
  ['足球', '⚽'], ['瓢虫', '🐞'], ['便便', '💩'], ['月亮', '🌙'], ['太阳', '☀️'], ['礼物', '🎁'],
  ['拥抱', '🤗'], ['强', '👍'], ['弱', '👎'], ['握手', '🤝'], ['胜利', '✌️'], ['抱拳', '🙏'],
  ['勾引', '😏'], ['拳头', '👊'], ['差劲', '👎'], ['爱你', '🤟'], ['NO', '🙅'], ['OK', '👌'],
  ['爱情', '💑'], ['飞吻', '😘'], ['跳跳', '🤸'], ['发抖', '🥶'], ['怄火', '😡'], ['转圈', '🌀'],
  ['磕头', '🙇'], ['回头', '🙄'], ['跳绳', '🤾'], ['挥手', '👋'], ['激动', '🤩'], ['街舞', '🕺'],
  ['献吻', '😙'], ['左太极', '☯️'], ['右太极', '☯️'],
]

// 输入条图标
const Svg = ({ children }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
)
const IconPlus = () => <Svg><path d="M12 5v14M5 12h14" /></Svg>
const IconMic = () => <Svg><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3M9 21h6" /></Svg>
const IconEmoji = () => <Svg><circle cx="12" cy="12" r="9" /><path d="M8.5 10h.01M15.5 10h.01M8 15a4 4 0 0 0 8 0" /></Svg>
const IconUp = () => <Svg><path d="M12 19V5M5 12l7-7 7 7" /></Svg>
const IconTrash = () => <Svg><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" /></Svg>
const IconCopy = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="12" height="12" rx="2.5" /><path d="M5 15V5a2 2 0 0 1 2-2h8" /></svg>
)
const IconUndo = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"><path d="M9 14L4 9l5-5" /><path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" /></svg>
)
// 引用回复：右上带折角的对话框
const IconQuote = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"><path d="M10 11H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v6c0 2-1 3-3 4" /><path d="M19 11h-4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v6c0 2-1 3-3 4" /></svg>
)
// 引用的原话最多取 15 字，跟后端 index.mjs 的 quoteSnip 同口径（后端按库里原文重新算，这里只是给运营看）
const quoteSnip = (text) => {
  const chars = [...String(text || '').replace(/\s+/g, ' ').trim()]
  return chars.length > 15 ? chars.slice(0, 15).join('') + '…' : chars.join('')
}
const IconStop = () => <svg width="14" height="14" viewBox="0 0 14 14"><rect width="14" height="14" rx="2" fill="currentColor" /></svg>
const IconBack = () => <Svg><path d="M19 12H5M12 19l-7-7 7-7" /></Svg>
const IconPanel = () => <Svg><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M15 4v16" /></Svg>
const IconX = () => <Svg><path d="M18 6L6 18M6 6l12 12" /></Svg>

export default function Workbench() {
  const me = auth.agent
  const duty = useOutletContext()   // 侧栏的在线/接单状态，宠物卡片跟着显示
  const [tab, setTab] = useState('ai')
  const [search, setSearch] = useState('')
  const [outcome, setOutcome] = useState('')
  const [communicationDate, setCommunicationDate] = useState('')
  const [convs, setConvs] = useState([])
  const [counts, setCounts] = useState({})
  const [curId, setCurId] = useState(null)
  // 正在跟客户对话的会话要钉住：列表是按归属/权限过滤的，同步改归属、客户换大厅等都会让它
  // 从列表里消失，但运营的对话框不能因此突然空掉（Gavin 2026-10-13）。
  const [pinned, setPinned] = useState(null)
  // 当前会话已被判定不在列表视野里：保留对话框和已加载的消息，但不再每 10 秒标已读、
  // 不再反复拉取（否则一天能刷出上百个 403）。
  const detachedRef = useRef(false)
  const [detached, setDetached] = useState(false)
  const setDetachedBoth = useCallback(v => { detachedRef.current = v; setDetached(v) }, [])
  // 失权是明确状态，不是一次列表缺项。列表刷新不能自动解锁，只有人工重检通过才能恢复。
  const lostConvsRef = useRef(new Map())
  const curIdRef = useRef(null)
  const selectionEpoch = useRef(0)
  const messageRequest = useRef(0)
  const listRequest = useRef(0)
  const retryAccessRef = useRef(false)
  const [retryingAccess, setRetryingAccess] = useState(false)
  // 原归属回来接手时的专用说明（Gavin 2026-10-13）：自己从「接待中」变成「不是接待人」
  // 就说明对话被交还给了订单原归属，窗口保留只读并明说，别让运营以为是自己掉线了
  const [handoverNote, setHandoverNote] = useState('')
  // 手机端（≤767px）：会话列表、聊天区、客户详情是三个各占满屏的面板而不是三栏并排，
  // mobileDetails 控制客户详情面板是不是叠在最上面（2026-10-05 Gavin 要）
  const mobile = useMobile()
  const [mobileDetails, setMobileDetails] = useState(false)
  const [msgs, setMsgs] = useState([])
  const [draft, setDraft] = useState('')
  const [replies, setReplies] = useState([])
  const [showReplies, setShowReplies] = useState(false)
  const [editingReply, setEditingReply] = useState(null) // null=关闭, {}=新增, {...}=编辑自己的
  const [replyTab, setReplyTab] = useState('system') // 快捷话术面板顶部的「系统话术／我的话术」切换
  const [replyCat, setReplyCat] = useState('') // 分类横条选中的分类，''=全部
  const loadReplies = () => api.listQuickReplies().then(setReplies).catch(() => {})
  const [sending, setSending] = useState(false)
  const [err, setErr] = useState('')
  // 中性提示（不是错误）：比如"这通会话已转出"。单独一条，避免和红色报错混在一起，
  // 也不会被「切换会话清空报错」那次 effect 顺手抹掉。下次点开会话时自动消失。
  const [notice, setNotice] = useState('')
  const [renaming, setRenaming] = useState(null)
  const [transferring, setTransferring] = useState(null)
  const [reowning, setReowning] = useState(null)   // 管理员改归属的那通会话
  const [outcomeModal, setOutcomeModal] = useState(null)   // { convId, prev, next } 标成交/失败要先选日期
  const [downgradeModal, setDowngradeModal] = useState(null)   // { convId, prevLabel } 成交改跟进中要单独确认
  const [agents, setAgents] = useState([])
  const [media, setMedia] = useState([])
  const [showMedia, setShowMedia] = useState(false)
  const [mediaCat, setMediaCat] = useState('')        // 素材面板分类筛选，''=全部（跟快捷话术的 replyCat 一个套路）
  const [lightbox, setLightbox] = useState('')
  const [recording, setRecording] = useState(false)   // 正在录音
  const [recSec, setRecSec] = useState(0)             // 已录秒数，给运营看的进度
  const [pendingVoice, setPendingVoice] = useState(null) // {dataUrl, url, sec} 录完待发送，可试听可重录
  const [pendingImg, setPendingImg] = useState(null)     // {dataUrl, convId} 粘贴 / 选好的图片，预览确认后才发
  const imgInputRef = useRef(null)
  const fileInputRef = useRef(null)
  const [pendingFile, setPendingFile] = useState(null)
  const [fileProgress, setFileProgress] = useState(0)
  useEffect(() => { setPendingFile(null) }, [curId])
  const [quote, setQuote] = useState(null)   // {id, text, convId}
  const recRef = useRef(null)   // { mr, timer, sec, cancelled }
  const recGen = useRef(0)      // 切换客户/离开工作台时 +1，作废还在等麦克风授权的录音
  const [showPlus, setShowPlus] = useState(false)     // 输入条左边「+」展开的菜单
  const [showEmoji, setShowEmoji] = useState(false)   // 表情选择面板
  const composerAreaRef = useRef(null)   // 「+」菜单、话术面板、素材面板、表情面板（含里面的分类页）共用的外框
  // 点这个外框以外的任意地方，把四个面板（不管开的哪个、哪个分类页）都收起
  useEffect(() => {
    if (!showPlus && !showReplies && !showMedia && !showEmoji) return
    const onDocMouseDown = e => {
      if (composerAreaRef.current?.contains(e.target)) return
      setShowPlus(false); setShowReplies(false); setShowMedia(false); setShowEmoji(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [showPlus, showReplies, showMedia, showEmoji])
  // 置顶只存在本浏览器（Gavin 定：不动数据库），按账号分开存，同一台电脑换人登录互不影响
  const pinKey = `wb_pins_${auth.agent?.id}`
  const [pins, setPins] = useState(() => { try { return JSON.parse(localStorage.getItem(pinKey)) || [] } catch { return [] } })
  const togglePin = id => setPins(prev => {
    const next = prev.includes(id) ? prev.filter(x => x !== id) : [id, ...prev]
    try { localStorage.setItem(pinKey, JSON.stringify(next)) } catch {}
    return next
  })
  const [menuFor, setMenuFor] = useState(null)        // 哪个会话的「⋯」菜单开着
  const [showHist, setShowHist] = useState(() => { try { return localStorage.getItem('wb_show_hist') !== '0' } catch { return true } })
  const toggleHist = () => setShowHist(v => { try { localStorage.setItem('wb_show_hist', v ? '0' : '1') } catch {}; return !v })
  // 左栏（会话列表）、右栏（第三栏）宽度可手动拖拽调节，记在本机，字号跟着宽度等比缩放
  const [leftW, setLeftW] = useColWidth('wb_left_w', 320)
  const [histW, setHistW] = useColWidth('wb_hist_w', 320)
  const [playPct, setPlayPct] = useState(0)           // 试听进度，驱动波点变色
  const audioRef = useRef(null)
  // 安卓 App 物理返回键：先关弹出的面板，都关了才退出聊天回列表（优先级比 Layout 的抽屉高，见 mobile.js）
  useBackAction(mobile && !!curId, () => {
    if (lightbox) { setLightbox(''); return }
    if (mobileDetails) { setMobileDetails(false); return }
    if (showPlus || showReplies || showMedia || showEmoji) {
      setShowPlus(false); setShowReplies(false); setShowMedia(false); setShowEmoji(false)
      return
    }
    selectConv(null)
  }, 10)
  const taRef = useRef(null)
  const msgBoxRef = useRef(null)   // 消息滚动容器
  const stickRef = useRef(true)    // 用户当前是否贴在底部；往上翻了就是 false，不再自动跟随

  // 失权时冻结已加载快照、关闭自动读取；列表缺项不等于失权。
  // 迟到响应必须通过 selectionEpoch 校验，不能把旧会话/上次打开的403施加到新选择。
  const dropLostConvRef = useRef(() => {})

  const [picked, setPicked] = useState(new Set())   // 派单勾选的会话
  const canAssign = auth.isAdmin && tab === 'ai'    // 派单只在管理员的「AI接待」页签下开放
  useEffect(() => { setPicked(new Set()) }, [tab])  // 切页签清空勾选，避免误操作到别的列表

  // 顺序不能动：cur 必须在自己被 closedConv 读取之前声明。
  // 2026-10-13 生产白屏就是这两行被调换（closedConv 提到 cur 前面）导致的暂时性死区错误
  // （ReferenceError: Cannot access 'Q' before initialization，工作台首次渲染即崩）。
  // 可选链 ?. 保护不了未初始化的 lexical binding；ESLint 的 no-use-before-define 已开成 error 拦截。
  const cur = curId ? (convs.find(c => c.id === curId) || (pinned?.id === curId ? pinned : null)) : null
  const notListed = !!curId && !convs.some(c => c.id === curId)
  const closedConv = cur?.status === 'closed'
  // 列表缺项可能只是分页/筛选；只有新快照明确为活跃失败单才收起客服当前选择。
  // 不写 lostConvs：结果恢复或进入已关闭后仍可重新选择，也不修改真实会话状态。
  const hideFailedSelection = useCallback(c => {
    if (me.role !== 'agent' || c?.outcome !== 'failed' || !['ai', 'waiting_human', 'human'].includes(c.status)) return false
    if (curIdRef.current !== c.id) return false
    selectionEpoch.current++
    curIdRef.current = null
    setCurId(null); setPinned(null); setMsgs([]); setDraft(''); setQuote(null)
    setDetachedBoth(false); setHandoverNote(''); setMobileDetails(false)
    setConvs(list => list.filter(row => row.id !== c.id))
    setNotice('该订单已标记失败，已从客服未关闭列表隐藏；售后仍可处理。')
    return true
  }, [me.role, setDetachedBoth])
  // 列表可更新快照，但不能证明详情权限恢复；明确失权后不再自动探测。
  useEffect(() => {
    if (!curId) { setPinned(p => (p ? null : p)); return }
    if (detachedRef.current) return
    const hit = convs.find(c => c.id === curId)
    if (hit) { if (!hideFailedSelection(hit)) setPinned(hit); return }
    // 接管成功退出AI列表或掉出前100条，仍要读一次详情，防止快照停在旧状态。
    let cancelled = false
    const epoch = selectionEpoch.current
    api.getConversation(curId)
      .then(c => { if (!cancelled && epoch === selectionEpoch.current && !detachedRef.current && c && !hideFailedSelection(c)) setPinned(c) })
      .catch(e => { if (!cancelled && epoch === selectionEpoch.current && e && isConversationLost(e)) dropLostConvRef.current(curId, lostConvMessage(e), e.code) })
    return () => { cancelled = true }
  }, [convs, curId, hideFailedSelection])
  // 置顶的排最前，其余保持后端给的顺序（sort 是稳定的）
  const shownConvs = [...convs].sort((a, b) => pins.includes(b.id) - pins.includes(a.id))
  useEffect(() => {
    if (!menuFor) return
    const close = () => setMenuFor(null)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [menuFor])
  const claimed = cur?.status === 'human' && (!cur.order_sync_enabled || cur.assigned_agent_id === me.id)
  // 交还提示不靠这里推断：claimed 下降也可能只是会话被关闭/释放，甚至 A 主动转给了 C。
  // 真正的交还说明由后端在请求时用 OWNER_RETURNED 带回来（见 dropLostConv）。
  // detached：该会话已不在列表视野（归属被同步改动等）。后端一定会 403 拒绝发送，
  // 所以这里直接禁掉输入，并在下方常驻一条只读说明，别让运营白打一段字。
  const canReply = !detached && !!cur && cur.status !== 'closed' && (auth.isAdmin || auth.isAftersales || claimed)
  const canRecall = !detached && !!cur && (auth.isAdmin || auth.isAftersales || claimed)
  const canClearReminder = c => auth.isAdmin || c.owner_agent_id === me.id ||
    (['human', 'waiting_human'].includes(c.status) && [c.assigned_agent_id, c.default_agent_id].includes(me.id))
  // 客户没回复之前，运营连续发的文字累计不超过 50 字（后端同样拦截，这里只是提前提示）
  // 每条不超过 50 字，不再按「客户回复前累计」算（2026-10-05 Gavin 定）；条数由企微 5 条额度管
  const draftLen = textLen(draft)
  // 系统话术（共享话术）不按 70 字算：可以删减后整段发，但不能新增内容（2026-10-12 Gavin 定）。
  // 判定与后端 backend/text.mjs 的 isAgentTextAllowed 同一套口径；后端才是真正的拦截。
  const systemReplies = replies.filter(r => !r.agent_id)
  const draftFromSystemReply = draftLen > TEXT_LIMIT && isAgentTextAllowed(draft, TEXT_LIMIT, systemReplies)
  const overLimit = draftLen > TEXT_LIMIT && !draftFromSystemReply
  // 企微 5 条 / 48 小时限制，只对企微会话生效；发满了整条输入都禁用，后端同样会拦。
  // 额度按客户跨会话算，由后端给；消息有变化（客户回话、我方发出）就重新取
  const [quota, setQuota] = useState({ left: null, msg: null })
  const deliveryRevision = msgs.map(m => `${m.id}:${m.role}:${m.meta?.delivery || ''}`).join('|')
  useEffect(() => {
    if (!cur?.id || detachedRef.current) return
    let alive = true
    const epoch = selectionEpoch.current
    api.getSendQuota(cur.id).then(q => { if (alive && epoch === selectionEpoch.current && !detachedRef.current) setQuota(q) }).catch(() => {})
    return () => { alive = false }
  }, [cur?.id, msgs.length, deliveryRevision, detached])
  const quotaMsg = quota.msg || ''
  const canSend = canReply && !quotaMsg
  const query = { search: search.trim() || undefined, outcome: outcome || undefined,
    communicationDate: tab === 'closed' ? communicationDate || undefined : undefined }

  const loadCounts = useCallback(async (q) => {
    try {
      const counts = await api.countConversations(q)
      setCounts(Object.fromEntries(TABS.map(t => [t.key, counts[t.key] || 0])))
    } catch { /* 角标不影响主流程，静默失败 */ }
  }, [])

  const loadList = useCallback(async (status, q) => {
    const request = ++listRequest.current
    try {
      const list = await api.listConversations({ status, limit: 100, ...q })
      if (request === listRequest.current) setConvs(list.filter(c => !lostConvsRef.current.has(c.id)))
    } catch (e) { if (request === listRequest.current) setErr(e.message) }
  }, [])

  function selectConv(id) {
    if (curIdRef.current === id) return
    selectionEpoch.current++
    curIdRef.current = id
    setPinned(convs.find(c => c.id === id) || null)
    const lost = lostConvsRef.current.get(id)
    setCurId(id); setDetachedBoth(!!lost)
    setHandoverNote(lost?.code === 'OWNER_RETURNED' ? lost.message : '')
    setNotice(lost?.message || '')
  }
  useEffect(() => {
    dropLostConvRef.current = (id, message, code) => {
      if (!id || lostConvsRef.current.has(id)) return
      lostConvsRef.current.set(id, { message, code })
      // 已经不在列表就保留原数组，不能用空变化触发详情effect；刷新返回也会过滤失权项。
      setConvs(list => list.some(c => c.id === id) ? list.filter(c => c.id !== id) : list)
      // 只在「正在看的就是这通」时提示；不是当前会话就静默摘掉，
      // 免得正看着 B 却弹出 A 的提示。
      // 关键：**不再清空对话框**。范围变化（同步改归属、客户换大厅）会让会话暂时查不到，
      // 但运营正在跟客户对话，突然把窗口清掉等于对话丢了（Gavin 2026-10-13）。
      // 只停掉轮询，消息和会话快照留在屏幕上。
      if (curIdRef.current === id) {
        if (code === 'CONVERSATION_GONE') {
          // 会话真被删除了：保留一个死窗口没有意义，照旧收起
          selectionEpoch.current++
          curIdRef.current = null
          setCurId(null); setMsgs([]); setQuota({ left: null, msg: null })
          setDetachedBoth(false); setHandoverNote(''); setNotice(message)
        } else {
          // 暂时失权（含被交还给原归属）：窗口保留只读，别把运营正在看的对话清掉
          setDetachedBoth(true)
          setNotice(message)
          setHandoverNote(code === 'OWNER_RETURNED' ? message : '')
        }
      }
      loadList(tab, query); loadCounts(query)
    }
  })
  const loadMsgs = useCallback(async (id) => {
    // 这里**不要** setMsgs([])：调用方可能拿着过期的 curId=null（未选会话时点的操作、请求完成
    // 前又打开了 B），会把 B 正在看的消息清掉，而且因为 id 为空绕过了下面的 ID 校验。
    // 「切走时清空」由 curId 的 effect 负责（Codex 60/66ef34a3 审查）。
    if (!id || lostConvsRef.current.has(id)) return
    const epoch = selectionEpoch.current
    const request = ++messageRequest.current
    const current = () => id === curIdRef.current && epoch === selectionEpoch.current && request === messageRequest.current
    try {
      const list = await api.listMessages(id)
      if (current() && !detachedRef.current) setMsgs(list)
    } catch (e) {
      if (!current()) return
      if (e && isConversationLost(e)) return dropLostConvRef.current(id, lostConvMessage(e), e.code)
      setErr(e.message)
    }
  }, [])

  // 只有用户主动重检才能解除熔断。详情和消息都成功后才恢复，失败保留旧消息/只读说明。
  async function retryConversationAccess() {
    const id = curIdRef.current
    if (!id || !detachedRef.current || retryAccessRef.current) return
    retryAccessRef.current = true; setRetryingAccess(true)
    const epoch = ++selectionEpoch.current
    const current = () => curIdRef.current === id && selectionEpoch.current === epoch
    try {
      const c = await api.getConversation(id)
      if (!current()) return
      if (!c || c.id !== id) throw new Error('会话详情未返回，请稍后重试')
      const list = await api.listMessages(id)
      if (!current()) return
      lostConvsRef.current.delete(id)
      if (hideFailedSelection(c)) return
      setPinned(c); setMsgs(list); setErr(''); setNotice(''); setHandoverNote('')
      setDetachedBoth(false)
      loadList(tab, query); loadCounts(query)
    } catch (e) {
      if (current()) {
        if (isConversationLost(e)) {
          // 删除或交还的类型可能变了：允许这次人工动作更新一次提示，而非启动后台循环。
          lostConvsRef.current.delete(id)
          dropLostConvRef.current(id, lostConvMessage(e), e.code)
        } else setErr(e.message)
      }
    } finally { retryAccessRef.current = false; setRetryingAccess(false) }
  }

  // 搜索防抖
  useEffect(() => {
    const t = setTimeout(() => { loadList(tab, query); loadCounts(query) }, 300)
    return () => clearTimeout(t)
  }, [tab, search, outcome, communicationDate])
  // 切换时先清空上一个客户的消息、额度和报错，新客户的加载完再显示
  useEffect(() => {
    if (curIdRef.current !== curId) selectionEpoch.current++
    curIdRef.current = curId
    setMsgs([]); setQuota({ left: null, msg: null }); setErr('')
    if (curId && !detachedRef.current) setNotice('')
    if (curId && !detachedRef.current) loadMsgs(curId)
  }, [curId, loadMsgs])

  // 打开会话即标已读；开着的时候也要持续标，否则客户在你眼皮底下发的新消息
  // 会被算成未读，角标一直亮着，运营根本不知道该点哪里才能消掉。
  // 标完把最新未读数广播出去，侧边栏立刻更新——否则要等它自己那轮 10 秒轮询，
  // 点开会话后角标还挂着好几秒，看着就像"点了没用"。
  useEffect(() => {
    if (!curId || detached) return
    let alive = true
    const epoch = selectionEpoch.current
    const current = () => alive && curIdRef.current === curId && epoch === selectionEpoch.current && !detachedRef.current
    const mark = () => {
      if (!current()) return
      api.markRead(curId)
        .then(r => {
          if (!current()) return
          if (r?.gone) return dropLostConvRef.current(curId, LOST_MESSAGES.CONVERSATION_GONE, 'CONVERSATION_GONE')
          window.dispatchEvent(new CustomEvent('unread-changed', { detail: r?.unread }))
        })
        .catch(e => { if (current() && e && isConversationLost(e)) dropLostConvRef.current(curId, lostConvMessage(e), e.code) })
    }
    mark()
    const t = setInterval(mark, 10 * 1000)
    return () => { alive = false; clearInterval(t) }
  }, [curId, msgs.length, detached])
  useEffect(() => { loadReplies() }, [])
  // 左下「设置 → 我的话术」里改了，快捷话术面板跟着刷新
  useEffect(() => {
    window.addEventListener('quick-replies-changed', loadReplies)
    return () => window.removeEventListener('quick-replies-changed', loadReplies)
  }, [])
  useEffect(() => { api.listAgents().then(setAgents).catch(() => {}) }, [])
  useEffect(() => { api.listMedia().then(setMedia).catch(() => {}) }, [])

  // 全量刷新：列表、角标、当前会话消息、运营在线/接单状态（转接/分配下拉框靠它）。
  // 消息不再按游标增量拉：撤回是改旧消息的 meta，增量拉不到，别人撤回的这边一直显示原文；
  // 现在只在有变化时才刷、后端又只给最近 200 条，全量拉的开销已经不是问题
  // 放进 ref 每次渲染后更新，定时器和事件回调永远拿到最新的页签/会话/筛选条件，不用把它们都列进依赖重建监听。
  // 在 effect 里赋值而不是渲染期间：严格模式下渲染可能被丢弃重跑，渲染期间写 ref 不保险
  const refreshAllRef = useRef(() => {})
  useEffect(() => {
    refreshAllRef.current = () => {
      loadList(tab, query); loadCounts(query); if (curId && !detachedRef.current) loadMsgs(curId)
      api.listAgents().then(setAgents).catch(() => {})
    }
  })
  // 300ms 内多次触发合并成一次：自己操作后的 refresh、后端随之而来的变化通知、点击刷新常常前后脚到。
  // 合并是「推后一次」不是「丢弃」——丢弃可能把紧跟在上一次拉取之后的写入漏掉
  const refreshGate = useRef({ last: 0, timer: null })
  const refreshSoon = useCallback(() => {
    const g = refreshGate.current
    const wait = 300 - (Date.now() - g.last)
    if (wait <= 0) { g.last = Date.now(); refreshAllRef.current(); return }
    if (!g.timer) g.timer = setTimeout(() => { g.timer = null; g.last = Date.now(); refreshAllRef.current() }, wait)
  }, [])
  useEffect(() => () => clearTimeout(refreshGate.current.timer), [])

  // 后端一有写入（客户来消息、AI 回复、转接、派单、改名……）就通过长轮询通知到这里（见 lib/changes.js），
  // 不再每 5 秒盲拉 4 个接口。页签在后台时只记一笔「有变化」，切回来再刷，省掉看不见的请求
  useEffect(() => {
    let dirty = false
    const off = onChange(() => { if (document.hidden) dirty = true; else refreshSoon() })
    const onVis = () => { if (!document.hidden && dirty) { dirty = false; refreshSoon() } }
    document.addEventListener('visibilitychange', onVis)
    return () => { off(); document.removeEventListener('visibilitychange', onVis) }
  }, [refreshSoon])

  // 兜底：运营心跳超时掉线这类不经过数据库写入的变化后端发不出通知，靠 60 秒一次的全量刷新补上；
  // 通知万一丢了也最多迟 1 分钟。素材库和共享话术也在这里顺带刷：管理员新传了素材、新加了话术，
  // 开着的工作台一分钟内自己就能看到，不用刷新页面（它们不在变化通知的范围里，改动也少，不值得单独通知）
  useEffect(() => {
    const t = setInterval(() => {
      if (document.hidden) return
      refreshSoon()
      api.listMedia().then(setMedia).catch(() => {})
      loadReplies()
    }, 60 * 1000)
    return () => clearInterval(t)
  }, [refreshSoon])

  // 页面上任意点一下就立刻刷新：客户被别人转走/派走后，不用干等通知（2026-09-30 Gavin 要求）。
  // 1 秒内多次点击只刷一次；用捕获阶段，点按钮、点列表项也都算
  useEffect(() => {
    let last = 0
    const onDown = () => {
      if (Date.now() - last < 1000) return
      last = Date.now()
      refreshSoon()
    }
    window.addEventListener('pointerdown', onDown, true)
    return () => window.removeEventListener('pointerdown', onDown, true)
  }, [refreshSoon])

  // 只有用户贴在底部时才自动跟随新消息，往上翻历史时不打扰。
  // 「贴底」必须在滚动时记下，不能等新消息渲染完再量：一条长回复或一张图本身就比阈值高，
  // 事后量会误判成「用户在翻历史」而不跟随。
  const toBottom = () => { const el = msgBoxRef.current; if (el) el.scrollTop = el.scrollHeight }
  // 往上翻时底部显示「N条消息」：数的是整条还在可视区下方、没看到的消息（系统提示不算）
  const [below, setBelow] = useState(0)
  const countBelow = () => {
    const el = msgBoxRef.current
    if (!el) return 0
    const edge = el.scrollTop + el.clientHeight
    return [...el.querySelectorAll('[data-msg]')].filter(n => n.offsetTop >= edge).length
  }
  const onMsgScroll = (e) => {
    const el = e.currentTarget
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
    setBelow(stickRef.current ? 0 : countBelow())
  }
  // 切换会话一律回到底部；此时消息还没加载完，靠 stickRef=true 让加载完的那次渲染接着滚
  useEffect(() => { stickRef.current = true; setBelow(0); toBottom() }, [curId])
  useEffect(() => { stickRef.current ? toBottom() : setBelow(countBelow()) }, [msgs])

  // detached（会话已交还/失权）时不再反复拉这通会话的消息，与另外两处守卫口径一致
  // 用 ref 取「此刻」的会话，而不是调用时的闭包值：迟到的 refresh() 不该去加载一个早就切走的会话
  const refresh = () => Promise.all([loadList(tab, query), loadCounts(query),
    ...(curIdRef.current && !detachedRef.current ? [loadMsgs(curIdRef.current)] : [])])

  // 转接/改归属成功之后：先看这通会话还在不在我的列表里，不在了就直接放手，
  // 不再对它发 listMessages（交出去的那通必然 403，线上每次转接都白打一个 500）。
  // 列表是分页的（前 100 通），"不在列表里"不等于"没权限"，所以再单独确认一次：
  // 还看得到就照常显示，确实看不了了才摘掉并说一句（不静默关掉运营正在看的会话）。
  async function refreshAfterHandoff(id, epoch) {
    const request = ++listRequest.current
    let list
    try {
      list = await api.listConversations({ status: tab, limit: 100, ...query })
      if (request === listRequest.current) setConvs(list.filter(c => !lostConvsRef.current.has(c.id)))
      loadCounts(query)
    } catch {
      // 列表没拉到就退回原来的全量刷新，别据此判定会话没了
      await Promise.all([loadList(tab, query), loadCounts(query),
        ...(epoch === selectionEpoch.current && id === curIdRef.current ? [loadMsgs(id)] : [])])
      return
    }
    if (!id || epoch !== selectionEpoch.current || id !== curIdRef.current || detachedRef.current) return
    if (list.some(c => c.id === id)) return loadMsgs(id)
    try {
      const c = await api.getConversation(id)
      if (epoch !== selectionEpoch.current || id !== curIdRef.current || detachedRef.current) return
      if (hideFailedSelection(c)) return
      if (c) setPinned(c)
      await loadMsgs(id)          // 还在权限范围内，只是掉出了前 100 条
    } catch (e) {
      if (epoch !== selectionEpoch.current || id !== curIdRef.current) return
      if (isConversationLost(e)) dropLostConvRef.current(id, lostConvMessage(e), e.code)
      else setErr(e.message)
    }
  }

  // 同一个动作 + 同一个目标在飞行中时，连点不再重复发请求。
  // 2026-10-12 实测：接管按钮 2 秒内被打 20 次（全被后端拒），那一分钟 /api/cs/admin 有 11 个 500。
  // 用 ref 同步挡（state 要等下一次渲染才变，连点之间来不及），按「动作:第一个参数」分键，
  // 不影响同时对不同会话做同一动作。
  const actBusy = useRef(new Set())
  async function act(fn, ...args) {
    const key = `${fn?.name || 'act'}:${String(args[0] ?? '')}`
    if (actBusy.current.has(key)) return
    actBusy.current.add(key)
    setErr('')
    try { await fn(...args); await refresh() } catch (e) { setErr(e.message) } finally { actBusy.current.delete(key) }
  }

  async function dismissPaymentReminder(c) {
    if (!canClearReminder(c)) return
    if (await confirmBox(c.order_sync_enabled && c.order_id
      ? '确认已核查并解除本订单所有会话的付款提醒？支付状态、订单归属和成交结果保持不变。'
      : '确认已核查并解除付款提醒？支付状态、订单归属和成交结果保持不变。')) {
      await act(api.clearPaymentVerification, c.id)
    }
  }

  // 发送中按钮会变灰，但输入框里连按 Enter 拦不住，上一条还没返回就发出第二条，客户收到两遍（审查 N08）。
  // 用 ref 同步挡：state 要等下一次渲染才变，两次 Enter 之间来不及
  const sendingRef = useRef(false)
  async function send() {
    const text = draft.trim()
    if (!text || !cur || !canReply || sendingRef.current) return
    if (quotaMsg) { setErr(quotaMsg); return }
    if (overLimit) {
      setErr(`单条最多 ${TEXT_LIMIT} 字，这条 ${draftLen} 字，请精简`)
      return
    }
    sendingRef.current = true
    setSending(true); setErr('')
    try {
      await api.sendAgentMessage(cur.id, text, me.id, quote?.convId === cur.id ? quote.id : undefined)
      setDraft('')
      setQuote(null)
      await refresh()
    } catch (e) { setErr(e.message) } finally { sendingRef.current = false; setSending(false) }
  }

  // ===== 语音 =====
  // 企微硬限制 60 秒，录满自动停——不能让运营录了 80 秒才发现发不出去。
  const VOICE_MAX_SEC = 60

  // 录音绑定开始时的客户：onstop 和读文件都是异步的，切换客户后才完成的话，
  // 不能让给 A 录的语音出现在 B 的输入条里（再按 Enter 就发给 B 了）

  const stopRecording = useCallback(() => {
    const r = recRef.current
    if (!r) return
    clearInterval(r.timer)
    if (r.mr.state !== 'inactive') r.mr.stop()   // onstop 里收尾
  }, [])

  const startRecording = useCallback(async () => {
    if (recRef.current || pendingVoice || !cur) return
    const convId = cur.id
    const gen = recGen.current
    setErr('')
    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      setErr('拿不到麦克风权限。请在系统应用设置或浏览器站点设置中允许使用麦克风后重试。')
      return
    }
    // 等权限弹窗期间切走了客户或离开了工作台：麦克风放掉，不录
    if (gen !== recGen.current) { stream.getTracks().forEach(t => t.stop()); return }
    const mr = new MediaRecorder(stream)
    const chunks = []
    const session = { mr, timer: null, sec: 0, cancelled: false }
    mr.ondataavailable = e => { if (e.data.size) chunks.push(e.data) }
    mr.onstop = () => {
      stream.getTracks().forEach(t => t.stop())
      recRef.current = null
      setRecording(false); setRecSec(0)
      if (session.cancelled) return
      const blob = new Blob(chunks, { type: chunks[0]?.type || 'audio/webm' })
      if (!blob.size || session.sec < 1) return   // 手滑点一下就松开，不生成空语音
      const reader = new FileReader()
      reader.onload = () => {
        if (session.cancelled || convId !== curIdRef.current) return
        setPendingVoice({ convId, dataUrl: reader.result, url: URL.createObjectURL(blob), sec: session.sec })
      }
      reader.readAsDataURL(blob)
    }
    const timer = setInterval(() => {
      const r = recRef.current
      if (!r) return
      r.sec += 1
      setRecSec(r.sec)
      if (r.sec >= VOICE_MAX_SEC) stopRecording()
    }, 1000)
    session.timer = timer
    recRef.current = session
    mr.start()
    setRecording(true); setRecSec(0)
  }, [cur, pendingVoice])

  function discardVoice() {
    if (pendingVoice?.url) URL.revokeObjectURL(pendingVoice.url)
    setPendingVoice(null); setPlayPct(0)
  }

  const [copiedId, setCopiedId] = useState(null)
  function copyMsg(m) {
    navigator.clipboard.writeText(m.content || '').then(() => {
      setCopiedId(m.id)
      setTimeout(() => setCopiedId(id => (id === m.id ? null : id)), 1500)
    }).catch(() => setErr('复制失败，请手动选中文字复制'))
  }

  async function recallMsg(m) {
    setErr('')
    try { await api.recallMessage(cur.id, m.id); await refresh() } catch (e) { setErr(e.message) }
  }

  function togglePlay() {
    const a = audioRef.current
    if (a) a.paused ? a.play() : a.pause()
  }

  // 输入框随内容长高，最多 6 行（CSS max-height），发完清空后缩回一行
  useEffect(() => {
    const t = taRef.current
    if (!t) return
    t.style.height = 'auto'
    t.style.height = `${t.scrollHeight}px`
  }, [draft])

  async function sendVoice() {
    if (!pendingVoice || !cur || pendingVoice.convId !== cur.id || sendingRef.current) return
    sendingRef.current = true
    setSending(true); setErr('')
    try {
      await api.sendVoice(cur.id, pendingVoice.dataUrl)
      discardVoice()
      await refresh()
    } catch (e) { setErr(e.message) } finally { sendingRef.current = false; setSending(false) }
  }

  // 发图片（2026-10-05 Gavin 要）：截图直接粘贴进输入框，或「+ → 图片」从电脑选。先预览再发。
  // 企微只收 JPG / PNG，别的格式（webp、gif、heic 等）先在浏览器里画一遍转成 PNG
  function pickImage(file) {
    if (!file?.type?.startsWith('image/') || !cur) return
    if (file.size > 10 * 1024 * 1024) return setErr('图片超过 10MB，企业微信发不了')
    const convId = cur.id
    const reader = new FileReader()
    reader.onload = () => {
      if (/^image\/(png|jpeg)$/.test(file.type)) return setPendingImg({ dataUrl: reader.result, convId })
      const img = new Image()
      img.onload = () => {
        const c = document.createElement('canvas')
        c.width = img.naturalWidth; c.height = img.naturalHeight
        c.getContext('2d').drawImage(img, 0, 0)
        setPendingImg({ dataUrl: c.toDataURL('image/png'), convId })
      }
      img.onerror = () => setErr('这张图片打不开，换一张试试')
      img.src = reader.result
    }
    reader.readAsDataURL(file)
  }

  async function sendImage() {
    if (!pendingImg || !cur || pendingImg.convId !== cur.id || sendingRef.current) return
    sendingRef.current = true
    setSending(true); setErr('')
    try {
      await api.sendImage(cur.id, pendingImg.dataUrl)
      setPendingImg(null)
      await refresh()
    } catch (e) { setErr(e.message) } finally { sendingRef.current = false; setSending(false) }
  }

  function pickFile(file) {
    if (!file || !cur || !canSend) return
    try {
      documentType(file)
      setErr(''); setFileProgress(0)
      setPendingFile({ file, convId: cur.id })
    } catch (e) { setErr(e.message) }
  }

  async function sendFile() {
    if (!pendingFile || pendingFile.convId !== cur?.id || !canSend || sendingRef.current) return
    sendingRef.current = true
    setSending(true); setErr(''); setFileProgress(0)
    try {
      const result = await api.sendFile(pendingFile.convId, pendingFile.file, setFileProgress)
      if (result?.warning) setNotice(result.warning)
      setPendingFile(null)
      await refresh()
    } catch (e) { setErr(e.message) } finally { sendingRef.current = false; setSending(false) }
  }

  // 按住空格录音。只在光标不在输入框时生效，否则运营打字打空格就开始录音了。
  // keydown 会因按键重复反复触发，靠 recRef 挡住重入。
  useEffect(() => {
    if (!cur || cur.status === 'closed') return
    const typing = () => {
      const t = document.activeElement?.tagName
      return t === 'INPUT' || t === 'TEXTAREA' || document.activeElement?.isContentEditable
    }
    const onDown = e => {
      if (e.code !== 'Space' || typing() || e.repeat) return
      if (!canSend || pendingVoice) return
      e.preventDefault()
      startRecording()
    }
    const onUp = e => {
      if (e.code !== 'Space' || !recRef.current) return
      e.preventDefault()
      stopRecording()
    }
    const onKey = e => {
      if (!pendingVoice || typing()) return
      if (e.key === 'Enter') { e.preventDefault(); sendVoice() }
      if (e.key === 'Escape') { e.preventDefault(); discardVoice() }
    }
    window.addEventListener('keydown', onDown)
    window.addEventListener('keyup', onUp)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onDown)
      window.removeEventListener('keyup', onUp)
      window.removeEventListener('keydown', onKey)
    }
  })

  // 切换会话时把没发出去的录音清掉，避免录给 A 的语音发给了 B；离开工作台时也要停，不然麦克风一直开着
  useEffect(() => {
    const cancelRec = () => {
      recGen.current++
      if (!recRef.current) return
      recRef.current.cancelled = true
      stopRecording()
    }
    cancelRec()
    discardVoice()
    return cancelRec
  }, [curId])

  function insertReply(r) {
    setDraft(d => (d ? d + '\n' : '') + r.content)
    setShowReplies(false)
    api.useQuickReply(r.id).catch(() => {})
  }

  async function delReply(r, e) {
    e.stopPropagation()
    if (!(await confirmBox('确认删除这条快捷回复？'))) return
    try { await api.deleteQuickReply(r.id); loadReplies() } catch (e2) { alertBox(e2.message) }
  }

  // 素材直接发给客户（后端负责传企微并写入会话记录），不经过输入框
  async function sendMedia(m) {
    if (!cur) return
    setShowMedia(false); setSending(true); setErr('')
    try {
      const result = await api.sendMedia(cur.id, m.id)
      if (result?.warning) setNotice(result.warning)
      await refresh()
    } catch (e) { setErr(e.message) } finally { setSending(false) }
  }

  const mediaByCat = media.reduce((acc, m) => {
    ;(acc[m.category || '未分类'] ||= []).push(m)
    return acc
  }, {})

  // 分类支持「父类/子类」写法（如"退款/拒付"），按完整分类字符串分组，
  // 组内保持子类聚在一起，不用再做一层树形展开，面板本来就不大，够用又不乱。
  function groupByCategory(list) {
    const acc = list.reduce((a, r) => { (a[r.category || '未分类'] ||= []).push(r); return a }, {})
    return Object.entries(acc).sort(([a], [b]) => a.localeCompare(b))
  }

  // 快捷码直接接文本展开：光标前那个不含空白的词一旦跟某条快捷码完全一致就立刻替换成正文，
  // 不用额外按键——像输入法自造词一样即打即出，前提是快捷码本身够短够独特（约定以 / 开头）。
  function onDraftChange(e) {
    const val = e.target.value
    const token = val.match(/(\S+)$/)?.[1]
    const hit = token && replies.find(r => r.shortcut && r.shortcut === token)
    if (hit) {
      setDraft(val.slice(0, val.length - token.length) + hit.content)
      api.useQuickReply(hit.id).catch(() => {})
      return
    }
    setDraft(val)
  }

  return (
    <div className={`wb${curId ? ' mobile-chat' : ''}`} style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* 顶部：标题 + 四个状态页签一排 */}
      <div className="wb-toolbar" style={{ padding: '16px 24px 14px', background: 'var(--card)' }}>
        <div className="row wb-toolbar-row" style={{ gap: 20 }}>
          {!auth.isAftersales && <OperatorStats agentId={me.id} />}
          <div className="row wb-tabs" style={{ gap: 4, background: 'var(--hover)', padding: 4, borderRadius: 999 }}>
            {TABS.map(t => {
              const on = t.key === tab
              // 有客户在等人接、而运营正看着别的页签：「待接入」的数字角标变红，一眼就能看到
              const alarm = !on && t.key === 'waiting_human' && counts[t.key] > 0
              return (
                <button key={t.key} onClick={() => { setTab(t.key); selectConv(null) }}
                  style={{
                    border: 'none', borderRadius: 999, padding: '7px 16px', fontSize: 14, cursor: 'pointer',
                    background: on ? 'var(--card)' : 'transparent',
                    color: on ? 'var(--text)' : 'var(--muted)',
                    fontWeight: on ? 600 : 400,
                    boxShadow: on ? '0 1px 3px rgba(0,0,0,.08)' : 'none',
                  }}>
                  {t.label}
                  {counts[t.key] > 0 && (
                    <span style={{
                      marginLeft: 7, padding: '1px 7px', borderRadius: 20, fontSize: 12,
                      background: alarm ? 'var(--danger)' : on ? 'var(--brand)' : 'var(--muted)', color: '#fff',
                    }}>{counts[t.key]}</span>
                  )}
                </button>
              )
            })}
          </div>
          <DispatchNotice key={me.id} agentId={me.id} />
        </div>
      </div>

      <div className="wb-columns" style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      {/* 左：搜索 + 会话列表。外层固定宽度不缩放，内层按 zoom 整体放大/缩小字号和间距，
          内层宽度按 1/zoom 换算，缩放后刚好撑满外层——避免「拖多宽、缩放完更宽」的失真。
          手机端（mobile.css 的 .wb-list/.wb-list-inner 规则）强制 zoom:1、宽度 100%，桌面端这两行不变 */}
      <div className="wb-list" style={{ width: leftW, flexShrink: 0, overflow: 'hidden' }}>
      <div className="wb-list-inner" style={{ width: leftW / colZoom(leftW), height: '100%', zoom: colZoom(leftW), background: 'var(--card)', display: 'flex', flexDirection: 'column' }}>
        <div className="row" style={{ padding: 12, gap: 8 }}>
          <input className="input" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="搜索手机号 / 车牌 / 名称" style={{ flex: 1, minWidth: 0 }} />
          <ContentSelect aria-label="筛选结果" label={OUTCOME_FILTERS.find(o => o.key === outcome)?.label}
            value={outcome} onChange={e => setOutcome(e.target.value)}>
            {OUTCOME_FILTERS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </ContentSelect>
        </div>
        {tab === 'closed' && <div className="row wb-date-filter">
          <label htmlFor="wb-communication-date">沟通日期</label>
          <DateRangePicker id="wb-communication-date" aria-label="沟通日期" single
            from={communicationDate} to={communicationDate}
            onChange={date => { setCommunicationDate(date); selectConv(null) }} />
          {communicationDate && <button className="icon-button" title="清除沟通日期" aria-label="清除沟通日期"
            onClick={() => { setCommunicationDate(''); selectConv(null) }}>×</button>}
        </div>}
        {lostConvsRef.current.size > (detached ? 1 : 0) && <button className="btn btn-sm" style={{ margin: '0 12px 8px' }}
          onClick={() => {
            // 切走后仍有显式恢复入口；仅恢复候选列表，点击会话时还会重新鉴权。
            for (const id of lostConvsRef.current.keys()) if (id !== curIdRef.current) lostConvsRef.current.delete(id)
            loadList(tab, query); loadCounts(query)
          }}>重新显示已失权会话</button>}
        {/* 派单工具条：只有管理员、且在「AI接待」页签下出现。
            用途是把客户定向分给指定运营跟进，避免所有人都在公共池里挑肥拣瘦。 */}
        {canAssign && (
          <AssignBar convs={convs} agents={agents} picked={picked} setPicked={setPicked}
            onDone={async (msg) => { setPicked(new Set()); setErr(msg); await refresh() }} />
        )}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {convs.length === 0 && <div className="empty">{search ? '没有匹配的客户' : '暂无会话'}</div>}
          {shownConvs.map(c => (
            <div key={c.id} onClick={() => { setMobileDetails(false); selectConv(c.id) }} className={`wb-conv${c.id === curId ? ' on' : ''}`}
              style={{ display: 'flex', gap: 11, padding: '12px 12px', cursor: 'pointer' }}>
              {canAssign && (
                <input type="checkbox" checked={picked.has(c.id)} style={{ marginTop: 12, cursor: 'pointer' }}
                  onClick={e => e.stopPropagation()}
                  onChange={e => {
                    const n = new Set(picked)
                    e.target.checked ? n.add(c.id) : n.delete(c.id)
                    setPicked(n)
                  }} />
              )}
              {/* 表情头像：成交笑脸带金冠（镂空=AI独立，实心=人工介入过），
                  失败哭脸带闪电，跟进中/未标记是正常脸，一眼看出这单的状态 */}
              <FaceAvatar outcome={c.outcome} source={c.outcome_source} unread={c.unread_count} />
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* 第一行：车牌 + 标签；第二行归属和当前接待；第三行时间 */}
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <strong style={{ fontSize: 14, flexShrink: 0 }}>
                    {pins.includes(c.id) && <span title="已置顶" style={{ marginRight: 4 }}>📌</span>}
                    {c.nickname || c.user_id?.replace('wecom:', '').slice(0, 12) || '未知'}
                  </strong>
                  <div className="row conv-tags" style={{ gap: 5, flexWrap: 'wrap', maxWidth: '100%',
                    flexBasis: c.order_payment ? '100%' : undefined, justifyContent: c.order_payment ? 'flex-start' : 'flex-end' }}>
                    {/* 红标点一下确认解除；成交的单也显示——成交后还挂着的就是激活后的投诉 */}
                    {c.risk_flag && (
                      <span className="tag" title="处理完了点一下，确认解除"
                        style={{ background: '#dc2626', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                        onClick={async e => {
                          e.stopPropagation()
                          const label = RISK_LABEL[c.risk_flag] || '风险'
                          if (await confirmBox(`是否已解除「${label}」？`)) act(api.clearRiskFlag, c.id, label)
                        }}>
                        ⚠ {RISK_LABEL[c.risk_flag] || '需介入'}
                      </span>
                    )}
                    {/* 客户说已付款但还没标结果——钱可能已经进来了，只差登记 */}
                    {c.verify_flag && (!c.outcome || c.outcome === 'following') && (
                      <button className="tag" title="核查后解除付款提醒" disabled={!canClearReminder(c)}
                        onClick={e => { e.stopPropagation(); dismissPaymentReminder(c) }}
                        style={{ background: '#f59e0b', color: '#fff', fontWeight: 600, border: 0, cursor: 'pointer' }}>
                        待核实标记
                      </button>
                    )}
                    {c.plate_other_orders > 0 && <span className="tag" style={{ background: '#e0f2fe', color: '#075985' }} title="同一个车牌在系统里有多张订单，各自是独立会话，不会合并，注意别漏看">同车牌另有 {c.plate_other_orders} 单</span>}
                    {c.owner_locked && <span className="tag" style={{ background: '#fee2e2', color: '#991b1b' }}>归属锁定</span>}
                    {c.owner_manual && !c.owner_locked && <span className="tag" style={{ background: '#ede9fe', color: '#5b21b6' }} title="管理员指定的归属：别人帮忙处理、归属人离线都不会改">指定归属</span>}
                    {c.pending_outcome && <span className="tag" style={{ background: '#e5e7eb', color: '#4b5563' }}>{outcomeLabel(c.pending_outcome)} · 核验中</span>}
                    {isSuccess(displayOutcome(c)) && <span className="tag tag-human">{outcomeLabel(displayOutcome(c))}</span>}
                    {displayOutcome(c) === 'following' && <span className="tag" style={{ background: '#fef3c7', color: '#92400e' }}>跟进中</span>}
                    {displayOutcome(c) === 'failed' && <span className="tag" style={{ background: '#fef2f2', color: '#dc2626' }}>失败</span>}
                  </div>
                </div>
                <div className="small muted" style={{ marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: '2px 10px' }}>
                  <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    归属：{c.owner_agent_id
                      ? (c.owner_agent_id === me.id
                          ? <span style={{ color: 'var(--brand)', fontWeight: 500 }}>我</span>
                          : (c.owner_name || c.owner_agent_id))
                      : '无'}
                  </span>
                  <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>接待人：{receptionLabel(c, agents)}</span>
                </div>
                <div className="muted small wb-hover" style={{ marginTop: 3 }}>{fmtTime(c.last_message_at)}</div>
              </div>
              {/* 「⋯」在最右侧、上下居中；跟时间一样鼠标移上去才出现，菜单开着时保持显示 */}
              <div className={`wb-hover${menuFor === c.id ? ' keep' : ''}`} style={{ position: 'relative', alignSelf: 'center', flexShrink: 0 }}>
                <button className="wb-more" title="更多" onClick={e => { e.stopPropagation(); setMenuFor(menuFor === c.id ? null : c.id) }}>⋯</button>
                {menuFor === c.id && (
                  <div className="wb-menu" onClick={e => e.stopPropagation()}>
                    <button onClick={() => { togglePin(c.id); setMenuFor(null) }}>
                      {pins.includes(c.id) ? '取消置顶' : '置顶'}
                    </button>
                    {/* 管理员可直接转接，普通运营仍需先接管。 */}
                    {!auth.isAftersales && <button disabled={c.status === 'closed' || (!auth.isAdmin && c.status !== 'human')} title={!auth.isAdmin && c.status !== 'human' ? '接管后才能转接' : ''}
                      onClick={() => {
                        setMenuFor(null)
                        // 列表只在进页面时拉过一次，开久了在线状态是陈的，转接前刷一次
                        api.listAgents().then(setAgents).catch(() => {})
                        setTransferring({ ...c, viewEpoch: selectionEpoch.current })
                      }}>转接</button>}
                    {!auth.isAftersales && <button onClick={() => { setMenuFor(null); setRenaming(c) }}>改名</button>}
                    {/* 只有管理员有：锁定的、归属人离线的也能直接改（2026-10-05 Gavin 定） */}
                    {auth.isAdmin && (
                      <button onClick={() => { setMenuFor(null); api.listAgents().then(setAgents).catch(() => {}); setReowning({ ...c, viewEpoch: selectionEpoch.current }) }}>改归属</button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>

      <ColResizer width={leftW} setWidth={setLeftW} side="left" />

      {/* 对话区 */}
      <div className="wb-chat" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative', background: 'var(--card)' }}>
        {/* 手机端顶栏：返回列表 + 客户名 + 打开详情（桌面端不显示，见 mobile.css .mobile-only） */}
        <div className="mobile-only wb-chat-nav">
          <button className="icon-button" title="返回会话列表" aria-label="返回会话列表" onClick={() => { setMobileDetails(false); selectConv(null) }}><IconBack /></button>
          <strong>{cur?.nickname || cur?.user_id?.replace('wecom:', '') || '会话'}</strong>
          <button className="icon-button" title="客户详情与历史" aria-label="客户详情与历史" disabled={!cur} onClick={() => setMobileDetails(true)}><IconPanel /></button>
        </div>
        <button className={`wb-fold${showHist ? '' : ' folded'}`} onClick={toggleHist} title={showHist ? '收起右栏' : '展开右栏'}>
          {showHist ? '›' : '‹'}
        </button>
        {!cur ? (
          <div className="empty" style={{ margin: 'auto' }}>请选择左侧会话</div>
        ) : (
          <>
            <div className="spread wb-head" style={{ margin: '10px 16px 4px', padding: '8px 10px 8px 12px', borderRadius: 999, background: 'var(--hover)' }}>
              <div className="row" style={{ gap: 10 }}>
                {!auth.isAftersales && <ContentSelect aria-label="办理结果"
                  label={outcomeLabel(cur.pending_outcome || displayOutcome(cur)) || resultPlaceholder(cur)}
                  value={cur.pending_outcome || displayOutcome(cur)}
                  disabled={!!cur.pending_outcome || (cur.order_sync_enabled && !cur.order_id)}
                  onChange={async e => {
                    const next = e.target.value || null
                    // 成交/失败要先选日期（可以补标成之前真正发生的那天，统计按这天算）
                    if (next && (isSuccess(next) || next === 'failed')) {
                      setOutcomeModal({ convId: cur.id, prev: cur.outcome, next,
                        payment: cur.order_payment, verify: cur.order_sync_enabled && requiresPaymentVerification(next) })
                      return
                    }
                    // 已经标过结果，不允许再改回未标记结果
                    if (next === null) {
                      if (cur.outcome) alertBox('已经标记过结果，不能改回「未标记结果」')
                      return
                    }
                    if (cur.outcome && cur.outcome !== next) {
                      // 成交改跟进中：专门确认一下是不是真的还没支付，别手滑把到账的单退回去
                      if (requiresPaymentVerification(cur.outcome)) {
                        setDowngradeModal({ convId: cur.id, prevLabel: outcomeLabel(cur.outcome) })
                        return
                      }
                      const msg = `当前已标记为「${outcomeLabel(cur.outcome)}」，改成「跟进中」？\n修改后只按最新状态计一次，不会重复计成交。`
                      if (!(await confirmBox(msg))) return
                    }
                    act(api.setOutcome, cur.id, next)
                  }}>
                  <option value="" disabled={!!cur.outcome}>{resultPlaceholder(cur)}</option>
                  {SELECTABLE_OUTCOMES.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                  {/* 已下架的老选项（未细分的成功、67不退），旧记录选中时得能显示出来，否则下拉会显示成空白 */}
                  {cur.outcome && !SELECTABLE_OUTCOMES.some(o => o.key === cur.outcome) &&
                    <option value={cur.outcome}>{outcomeLabel(cur.outcome)}</option>}
                </ContentSelect>}
                <strong>{cur.nickname || cur.user_id?.replace('wecom:', '').slice(0, 18)}</strong>
              </div>
              {!auth.isAftersales && <div className="row" style={{ gap: 8 }}>
                {cur.status !== 'closed' && (cur.status !== 'human' ||
                  (auth.isAdmin && cur.order_sync_enabled && cur.assigned_agent_id !== me.id)) && (
                  <button className="btn btn-sm btn-primary" onClick={async () => {
                    if (cur.status === 'human' && !(await confirmBox('确认接管当前运营正在接待的会话？订单归属不变。'))) return
                    act(api.claim, cur.id, me.id)
                  }}>接管</button>
                )}
                {(claimed || (auth.isAdmin && cur.status === 'human')) && (
                  <button className="btn btn-sm" onClick={() => act(api.release, cur.id)}>释放给AI</button>
                )}
                {/* 管理员可管理AI会话；普通运营保留原结束入口。 */}
                {(cur.status === 'human' || cur.status === 'waiting_human' || (auth.isAdmin && cur.status === 'ai')) && (
                  <button className="btn btn-sm btn-danger"
                    onClick={async () => (await confirmBox('确认结束该会话？')) && act(api.close, cur.id)}>结束会话</button>
                )}
              </div>}
            </div>

            {cur.order_sync_enabled && <OrderSyncStatus key={cur.id} conv={cur} onChanged={refresh} />}
            {!cur.order_sync_enabled && <LegacyOrderStatus conv={cur} />}

                  {(detached || notListed) && (
                    <div style={{ fontSize: 12, lineHeight: 1.5, color: '#b45309', background: '#fffbeb',
                      borderTop: '1px solid #fde68a', padding: '6px 10px' }}>
                      {handoverNote || (detached
                        ? '查看权限已变化，自动刷新已暂停；保留已加载聊天。确认权限恢复后，可点击「重新检查权限」。'
                        : closedConv ? '这通会话已结束（只读），保留窗口方便回看。'
                          : '该会话不在当前筛选列表，窗口继续保留；操作权限以最新详情为准。')}
                      {detached && <button className="btn btn-sm" style={{ marginLeft: 8 }} disabled={retryingAccess}
                        onClick={retryConversationAccess}>{retryingAccess ? '检查中…' : '重新检查权限'}</button>}
                    </div>
                  )}

            {/* 客户说已付款却还没标结果：直接在会话顶部横幅提醒，不然点进来还得自己留意 */}
            {cur.verify_flag && (!cur.outcome || cur.outcome === 'following') && (
              <div style={{
                margin: '6px 16px 0', padding: '8px 12px', borderRadius: 6,
                background: '#fffbeb', color: '#92400e', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span style={{ flex: 1, minWidth: 0 }}>客户可能已付款，请核实是否到账及本单办理方式</span>
                {canClearReminder(cur) && <button className="cb-icon" style={{ flexShrink: 0 }} title="解除付款提醒"
                  aria-label="解除付款提醒" onClick={() => dismissPaymentReminder(cur)}><IconX /></button>}
              </div>
            )}

            <div style={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex' }}>
            {/* 图片/视频是渲染后才加载出来的，撑高了对话框要再滚一次，否则停在图片上方。
                position:relative 让消息行的 offsetTop 以它为基准，countBelow 靠这个算 */}
            <div className="wb-messages" ref={msgBoxRef} style={{ flex: 1, overflow: 'auto', padding: '20px 28px', background: 'var(--chat-bg)', position: 'relative' }} onScroll={onMsgScroll}
              onLoadCapture={() => stickRef.current && toBottom()}
              onLoadedMetadataCapture={() => stickRef.current && toBottom()}>
              {msgs.map(m => {
                if (m.role === 'system') {
                  return <div key={m.id} className="muted small" style={{ textAlign: 'center', margin: '12px 0' }}>{m.content}</div>
                }
                const mine = m.role === 'assistant' || m.role === 'agent'
                const sentMed = findSentMedia(m, media)
                const who = m.role === 'user' ? '客户'
                  : m.role === 'agent' ? (agents.find(a => a.id === m.agent_id)?.name || m.agent_id || '人工') : 'AI小助'
                if (m.meta?.recalled_at) {
                  const recalledBy = m.meta.recalled_by
                    ? (agents.find(a => a.id === m.meta.recalled_by)?.name || m.meta.recalled_by) : who
                  // 撤回后客户那边看不到了，我方自己撤回的留原文方便改了重发（像微信的「重新编辑」）；
                  // 客户撤回的不出这个按钮——那是客户自己的话，不是我们要改了重发的东西（2026-10-05 Gavin 指出）
                  return (
                    <div key={m.id} data-msg className="muted small" style={{ textAlign: 'center', margin: '12px 0' }}>
                      {recalledBy}撤回了一条消息
                      {!mine || m.meta.media || sentMed ? null : (
                        <button className="btn-link small" style={{ marginLeft: 8 }} title={m.content}
                          onClick={() => { setDraft(m.content); taRef.current?.focus() }}>重新编辑</button>
                      )}
                    </div>
                  )
                }
                return (
                  <div key={m.id} data-msg style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 16 }}>
                    <div style={{ maxWidth: '70%', display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
                      <div className="muted small" style={{ marginBottom: 4, padding: '0 10px', fontSize: 12 }}>
                        {who}{' · '}{fmtTime(m.created_at)}
                      </div>
                      <div className="msg-line" style={{ flexDirection: mine ? 'row-reverse' : 'row' }}>
                        {/* 客户灰气泡在左，我方蓝气泡在右；人工比 AI 深一档，扫一眼就分得清谁回的 */}
                        <div className={`bubble ${m.role === 'user' ? 'them' : m.role === 'agent' ? 'agent' : 'ai'}`}>
                          {m.meta?.media
                            ? <><MediaBubble media={m.meta.media} delivery={m.meta.delivery} onViewImage={setLightbox} />{(m.meta.asr || m.meta.vision || m.role === 'assistant') && <div className="small" style={{ marginTop: 6 }} title={m.role === 'assistant' ? 'AI 语音回复的文字内容' : '语音自动识别的文字'}>{m.meta.vision ? `识图：${m.meta.vision}` : m.content}</div>}</>
                            : sentMed ? <KbMediaBubble med={sentMed} onViewImage={setLightbox} />
                            : m.meta?.quote ? <><div className="quote-in-bubble">「{m.meta.quote.text}」</div>{m.content}</>
                            : m.content}
                        </div>
                        {!m.meta?.media && !sentMed && (
                          <button className="msg-act" title="复制" onClick={() => copyMsg(m)}>
                            {copiedId === m.id ? <span className="small">已复制</span> : <IconCopy />}
                          </button>
                        )}
                        {['user', 'assistant', 'agent'].includes(m.role) && canSend && (
                          <button className="msg-act" title="引用回复"
                            onClick={() => { setQuote({ id: m.id, text: quoteSnip(m.content), convId: cur.id }); taRef.current?.focus() }}><IconQuote /></button>
                        )}
                        {mine && canRecall && m.meta?.wecom_msgid && (
                          <button className="msg-act" title="撤回" onClick={() => recallMsg(m)}><IconUndo /></button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            {below > 0 && (
              <button className="jump-new" onClick={() => { stickRef.current = true; setBelow(0); toBottom() }}>
                {below}条消息
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                  strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
              </button>
            )}
            </div>

            {cur.status !== 'closed' && (
              <div className="wb-composer-area" ref={composerAreaRef} style={{ padding: '8px 28px 16px', background: 'var(--chat-bg)', position: 'relative' }}>
                {showReplies && (
                  <div className="card" style={{
                    position: 'absolute', bottom: '100%', left: 28, right: 28, marginBottom: 8,
                    maxHeight: 260, overflow: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,.1)', zIndex: 10,
                  }}>
                    <div className="row" style={{ padding: '10px 12px 0', gap: 6, borderBottom: '1px solid var(--line)' }}>
                      {[['system', '系统话术'], ['mine', '我的话术']].map(([key, label]) => (
                        <button key={key} className="btn btn-sm"
                          onClick={() => { setReplyTab(key); setReplyCat('') }}
                          style={{
                            marginBottom: 8,
                            ...(replyTab === key
                              ? { background: 'var(--brand)', borderColor: 'var(--brand)', color: '#fff' }
                              : {}),
                          }}>{label}</button>
                      ))}
                      {replyTab === 'mine' && (
                        <button className="btn-link" style={{ marginLeft: 'auto' }} onClick={() => setEditingReply({})}>+ 新增</button>
                      )}
                    </div>
                    {(() => {
                      const shown = replyTab === 'system' ? replies.filter(r => !r.agent_id) : replies.filter(r => r.agent_id === me.id)
                      if (!shown.length) return (
                        <div className="empty small">{replyTab === 'system' ? '暂无系统话术' : '还没有自己的话术，点右上角新增'}</div>
                      )
                      const groups = groupByCategory(shown)
                      const items = replyCat ? (groups.find(([c]) => c === replyCat)?.[1] || []) : shown
                      return (
                        <>
                          {groups.length > 1 && (
                            <div className="row small" style={{
                              padding: '8px 12px', gap: 14, overflowX: 'auto', color: 'var(--muted)',
                              borderBottom: '1px solid var(--line)', whiteSpace: 'nowrap',
                            }}>
                              <span onClick={() => setReplyCat('')}
                                style={{ cursor: 'pointer', color: !replyCat ? 'var(--brand)' : 'inherit', fontWeight: !replyCat ? 600 : 400 }}>全部</span>
                              {groups.map(([cat]) => (
                                <span key={cat} onClick={() => setReplyCat(cat)}
                                  style={{ cursor: 'pointer', color: replyCat === cat ? 'var(--brand)' : 'inherit', fontWeight: replyCat === cat ? 600 : 400 }}>
                                  {cat}
                                </span>
                              ))}
                            </div>
                          )}
                          {items.map(r => (
                            <div key={r.id} onClick={() => insertReply(r)}
                              style={{ padding: '10px 12px', borderBottom: '1px solid var(--line)', cursor: 'pointer' }}>
                              <div className="spread" style={{ marginBottom: 3 }}>
                                {r.shortcut ? <span className="tag">{r.shortcut}</span> : <span />}
                                {replyTab === 'mine' && (
                                  <div className="row small" style={{ gap: 8 }}>
                                    <span className="btn-link" onClick={e => { e.stopPropagation(); setEditingReply(r) }}>编辑</span>
                                    <span className="btn-link" style={{ color: 'var(--danger)' }} onClick={e => delReply(r, e)}>删除</span>
                                  </div>
                                )}
                              </div>
                              <div className="small">{r.content.slice(0, 70)}{r.content.length > 70 ? '…' : ''}</div>
                            </div>
                          ))}
                        </>
                      )
                    })()}
                  </div>
                )}
                {showMedia && (
                  <div className="card" style={{
                    position: 'absolute', bottom: '100%', left: 28, right: 28, marginBottom: 8,
                    maxHeight: 300, overflow: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,.1)', zIndex: 10,
                  }}>
                    {media.length === 0 && <div className="empty small" style={{ padding: 12 }}>暂无素材，请先到知识库上传</div>}
                    {Object.keys(mediaByCat).length > 1 && (
                      // 分类都摆在最上面一排（跟话术面板一个套路），点哪个下面就只显示哪个分类的素材
                      <div className="row small" style={{
                        padding: '8px 12px', gap: 14, overflowX: 'auto', color: 'var(--muted)',
                        borderBottom: '1px solid var(--line)', whiteSpace: 'nowrap',
                      }}>
                        <span onClick={() => setMediaCat('')}
                          style={{ cursor: 'pointer', color: !mediaCat ? 'var(--brand)' : 'inherit', fontWeight: !mediaCat ? 600 : 400 }}>全部</span>
                        {Object.keys(mediaByCat).map(cat => (
                          <span key={cat} onClick={() => setMediaCat(cat)}
                            style={{ cursor: 'pointer', color: mediaCat === cat ? 'var(--brand)' : 'inherit', fontWeight: mediaCat === cat ? 600 : 400 }}>
                            {cat}
                          </span>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(92px,1fr))', gap: 8, padding: 12 }}>
                      {(mediaCat ? mediaByCat[mediaCat] || [] : media).map(m => (
                        <div key={m.id} onClick={() => sendMedia(m)} title={`点击发送：${m.title}`}
                          style={{
                            cursor: 'pointer', border: '1px solid var(--line)', borderRadius: 8,
                            overflow: 'hidden', background: 'var(--card)',
                          }}>
                          {m.kind === 'file' ? (
                            <DocumentCard filename={documentDownloadName(m)} compact />
                          ) : m.kind === 'video' ? (
                            <div style={{
                              height: 66, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: 'var(--hover)', fontSize: 20, color: 'var(--muted)',
                            }}>▶ 视频</div>
                          ) : (
                            <img src={serverUrl(`/kb-media/${m.filename}`)} alt={m.title}
                              style={{ width: '100%', height: 66, objectFit: 'cover', display: 'block' }} />
                          )}
                          <div className="small" style={{
                            padding: '4px 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>{m.title}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {showPlus && (
                  <div className="cb-menu">
                    <button onClick={() => { setShowReplies(true); setShowMedia(false); setShowPlus(false); setShowEmoji(false) }}>话术</button>
                    <button disabled={sending} onClick={() => { setShowMedia(true); setMediaCat(''); setShowReplies(false); setShowPlus(false); setShowEmoji(false) }}>素材</button>
                    <button disabled={sending} title="也可以截图后直接在输入框里粘贴"
                      onClick={() => { setShowPlus(false); imgInputRef.current?.click() }}>图片</button>
                    <button disabled={sending} title="Word / PDF，最大 20MB"
                      onClick={() => { setShowPlus(false); fileInputRef.current?.click() }}>发送文件</button>
                  </div>
                )}

                {recording ? (
                  // 录音中：波点按已录时长占 60 秒上限的比例变红，一眼看出还能录多久
                  <div className="composer" style={{ alignItems: 'center' }}>
                    <button className="cb-icon gray" title="取消录音"
                      onClick={() => { recRef.current && (recRef.current.sec = 0); stopRecording() }}><IconTrash /></button>
                    <span className="cb-time" style={{ color: 'var(--danger)' }}>● {fmtDur(recSec)}</span>
                    <div className="cb-dots rec" style={{ '--pct': `${recSec / VOICE_MAX_SEC * 100}%` }}><i /></div>
                    <span className="small muted" style={{ flexShrink: 0 }}>
                      {recSec >= VOICE_MAX_SEC - 10 ? `还剩 ${VOICE_MAX_SEC - recSec} 秒` : '松开空格或点■结束'}
                    </span>
                    <button className="cb-icon red" title="结束录音" onClick={stopRecording}><IconStop /></button>
                  </div>
                ) : pendingVoice ? (
                  // 录完待发：点波点试听，Enter 发送，Esc 删除
                  <div className="composer" style={{ alignItems: 'center' }}>
                    <button className="cb-icon gray" title="删除重录 (Esc)" onClick={discardVoice} disabled={sending}><IconTrash /></button>
                    <span className="cb-time">{fmtDur(pendingVoice.sec)}</span>
                    <button className="cb-dots" title="点击试听" onClick={togglePlay}
                      style={{ '--pct': playPct ? `${playPct}%` : '100%' }}><i /></button>
                    {/* MediaRecorder 录的 webm 在 Chrome 里 duration 是 Infinity，进度只能拿录制秒数当分母 */}
                    <audio ref={audioRef} src={pendingVoice.url} style={{ display: 'none' }}
                      onTimeUpdate={e => setPlayPct(Math.min(100, e.currentTarget.currentTime / pendingVoice.sec * 100))}
                      onEnded={() => setPlayPct(0)} />
                    <button className="cb-icon blue" title="发送 (Enter)" onClick={sendVoice} disabled={sending}><IconUp /></button>
                  </div>
                ) : (
                  <>
                  {quote && quote.convId === cur.id && (
                    <div className="quote-bar">
                      <span>引用：「{quote.text}」</span>
                      <button title="取消引用" onClick={() => setQuote(null)}>×</button>
                    </div>
                  )}
                  <div className="composer">
                    <button className="cb-icon" title="话术 / 素材 / 图片 / 文件" disabled={!canSend}
                      onClick={() => { setShowPlus(v => !v); setShowReplies(false); setShowMedia(false); setShowEmoji(false) }}><IconPlus /></button>
                    <textarea ref={taRef} rows={1} value={draft} disabled={!canReply} maxLength={TEXT_LIMIT}
                      placeholder={canReply ? (quotaMsg || (mobile ? '输入消息' : `注意⚠️：${quota.left != null ? `客户回复前还能发${quota.left}条，` : ''}每条最多${TEXT_LIMIT}字`))
                        : mobile ? (cur.status === 'ai' ? 'AI 接待中' : '等待接管')
                          : cur.status === 'ai' ? 'AI 正在接待，点右上角「接管」后才能回复' : '先点右上角「接管」，才能回复客户'}
                      onChange={onDraftChange}
                      onFocus={() => { setShowReplies(false); setShowMedia(false); setShowPlus(false); setShowEmoji(false) }}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
                      onPaste={e => {
                        const f = [...(e.clipboardData?.items || [])].find(i => i.type.startsWith('image/'))?.getAsFile()
                        if (f && canSend) { e.preventDefault(); pickImage(f) }
                      }} />
                    <input ref={fileInputRef} type="file" accept={DOCUMENT_ACCEPT} style={{ display: 'none' }}
                      onChange={e => { pickFile(e.target.files?.[0]); e.target.value = '' }} />
                    <input ref={imgInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={e => { pickImage(e.target.files?.[0]); e.target.value = '' }} />
                    <button className="cb-icon" title="表情" disabled={!canSend}
                      onClick={() => { setShowEmoji(v => !v); setShowReplies(false); setShowMedia(false); setShowPlus(false) }}><IconEmoji /></button>
                    {draft.trim() ? (
                      <button className="cb-icon blue" title="发送 (Enter)" onClick={send}
                        disabled={!canSend || sending || overLimit}><IconUp /></button>
                    ) : (
                      <button className="cb-icon" title="点击录音（电脑上也可按住空格键）"
                        disabled={!canSend || sending} onClick={startRecording}><IconMic /></button>
                    )}
                  </div>
                  {showEmoji && (
                    <div className="card" style={{
                      position: 'absolute', bottom: '100%', left: 28, right: 28, marginBottom: 8,
                      maxHeight: 220, overflow: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,.1)', zIndex: 10,
                      display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))', gap: 2, padding: 8,
                    }}>
                      {WECOM_EMOJIS.map(([name, glyph]) => (
                        <button key={name} type="button" title={name}
                          onClick={() => setDraft(d => d + `[${name}]`)}
                          style={{
                            border: 'none', background: 'transparent', borderRadius: 6, cursor: 'pointer',
                            fontSize: 20, padding: '6px 0', lineHeight: 1,
                          }}
                          onMouseDown={e => e.preventDefault()}>{glyph}</button>
                      ))}
                    </div>
                  )}
                  </>
                )}

                {canReply && quotaMsg && (
                  <div className="small" style={{ marginTop: 6, padding: '0 12px', color: 'var(--danger)' }}>⚠️ {quotaMsg}</div>
                )}
                {canReply && !quotaMsg && overLimit && (
                  <div className="small" style={{ marginTop: 6, padding: '0 12px', color: 'var(--danger)' }}>
                    超出字数上限（每条最多 {TEXT_LIMIT} 字，这条 {draftLen} 字），请精简（系统话术可以删减后发送，不能新增）
                  </div>
                )}
                {canReply && !quotaMsg && draftFromSystemReply && (
                  <div className="small" style={{ marginTop: 6, padding: '0 12px', color: '#92400e' }}>
                    系统话术 {draftLen} 字：可以删减后发送，不能新增内容
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {err && <div style={{ padding: '8px 20px', background: '#fef2f2', color: 'var(--danger)', fontSize: 13 }}>{err}</div>}
        {notice && (
          <div style={{ padding: '8px 20px', background: '#fffbeb', color: '#92400e', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1 }}>{notice}</span>
            <button className="btn" style={{ padding: '1px 8px' }} onClick={() => setNotice('')}>知道了</button>
          </div>
        )}
        {lightbox && <ImageLightbox url={lightbox} onClose={() => setLightbox('')} />}
        {pendingFile && pendingFile.convId === cur?.id && (
          <div className="modal-bg" onClick={e => e.target === e.currentTarget && !sending && setPendingFile(null)}>
            <div className="modal" style={{ maxWidth: 440 }}>
              <h2>发送文件给客户</h2>
              <DocumentCard filename={pendingFile.file.name} size={pendingFile.file.size} />
              <div className="small muted" style={{ marginTop: 12 }}>Word / PDF 最大 20MB。算 1 条，不加入素材库。</div>
              {sending && <div className="small" role="status">{fileProgress < 100 ? `上传中 ${fileProgress}%` : '上传完成，正在发送…'}</div>}
              {err && <div className="err">{err}</div>}
              <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
                <button className="btn" disabled={sending} onClick={() => setPendingFile(null)}>取消</button>
                <button className="btn btn-primary" disabled={sending || !canSend} onClick={sendFile}>{sending ? '发送中…' : '发送'}</button>
              </div>
            </div>
          </div>
        )}
        {pendingImg && pendingImg.convId === cur?.id && (
          <div className="modal-bg" onClick={e => e.target === e.currentTarget && !sending && setPendingImg(null)}>
            <div className="modal" style={{ width: 'auto', maxWidth: '90vw' }}>
              <h2>发送图片给客户</h2>
              <img src={pendingImg.dataUrl} alt="待发送图片"
                style={{ display: 'block', maxWidth: '80vw', maxHeight: '60vh', margin: '0 auto', borderRadius: 6, border: '1px solid var(--line)' }} />
              <div className="small muted" style={{ marginTop: 8 }}>算 1 条（企微规定客户回复前最多发 5 条）</div>
              {err && <div className="err">{err}</div>}
              <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
                <button className="btn" disabled={sending} onClick={() => setPendingImg(null)}>取消</button>
                <button className="btn btn-primary" disabled={sending || !canSend} onClick={sendImage}>{sending ? '发送中…' : '发送'}</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 第三栏：这个客户以前每次进线的记录，老客户回来一眼看到之前聊到哪 */}
      {/* 第三栏：上面是自己的宠物（一直在），下面是选中客户的历史会话 */}
      {showHist && <ColResizer width={histW} setWidth={setHistW} side="right" />}
      {mobile && mobileDetails && <button className="details-backdrop" aria-label="关闭客户详情" onClick={() => setMobileDetails(false)} />}
      {(showHist || (mobile && mobileDetails)) && (
        <aside className={`wb-hist${mobileDetails ? ' mobile-details' : ''}`} style={{ width: histW, flexShrink: 0, overflow: 'hidden' }}
          role={mobile && mobileDetails ? 'dialog' : undefined} aria-label="客户详情与历史" aria-modal={mobile && mobileDetails ? true : undefined}>
        <div className="wb-hist-inner" style={{ width: histW / colZoom(histW), height: '100%', zoom: colZoom(histW), display: 'flex', flexDirection: 'column' }}>
          <div className="mobile-only details-title">
            <strong>客户详情与历史</strong>
            <button className="icon-button" title="关闭客户详情" aria-label="关闭客户详情" onClick={() => setMobileDetails(false)}><IconX /></button>
          </div>
          {cur && <section className="mobile-only customer-summary">
            <dl>
              <dt>客户</dt><dd>{cur.nickname || cur.user_id}</dd>
              <dt>标识</dt><dd>{cur.user_id}</dd>
              <dt>归属</dt><dd>{cur.owner_name || cur.owner_agent_id || '无'}</dd>
              <dt>状态</dt><dd>{STATUS_TAG[cur.status]?.[1] || cur.status}</dd>
              <dt>结果</dt><dd>{outcomeLabel(displayOutcome(cur)) || resultPlaceholder(cur)}</dd>
            </dl>
          </section>}
          <Pet duty={duty} />
          {cur
            ? <CustomerHistory userId={cur.user_id} orderId={cur.order_id} currentId={cur.id} agents={agents} media={media}
                onViewImage={setLightbox} />
            : <div className="muted small" style={{ padding: '8px 26px' }}>选中左侧会话，这里显示该客户的历史会话</div>}
        </div>
        </aside>
      )}
      </div>

      {transferring && (
        <TransferModal conv={transferring} agents={agents} me={me}
          onClose={() => setTransferring(null)}
          onSaved={async () => { setTransferring(null); await refreshAfterHandoff(transferring.id, transferring.viewEpoch) }} />
      )}

      {reowning && (
        <OwnerModal conv={reowning} agents={agents}
          onClose={() => setReowning(null)}
          onSaved={async () => { setReowning(null); await refreshAfterHandoff(reowning.id, reowning.viewEpoch) }} />
      )}

      {renaming && (
        <RenameModal conv={renaming} onClose={() => setRenaming(null)}
          onSaved={async () => { setRenaming(null); await Promise.all([loadList(tab), loadCounts()]) }} />
      )}

      {outcomeModal?.verify && (
        <PaymentCheckModal outcome={outcomeModal.next} payment={outcomeModal.payment} onClose={() => setOutcomeModal(null)}
          onConfirm={async amount => {
            await api.setOutcome(outcomeModal.convId, outcomeModal.next, undefined, amount, outcomeModal.payment?.historical === true)
            setOutcomeModal(null)
            await refresh()
          }} />
      )}
      {outcomeModal && !outcomeModal.verify && (
        <OutcomeDateModal
          prevLabel={outcomeModal.prev ? outcomeLabel(outcomeModal.prev) : null}
          nextLabel={outcomeLabel(outcomeModal.next)}
          isFailed={outcomeModal.next === 'failed'}
          isCredit={outcomeModal.next === 'success_credit'}
          onClose={() => setOutcomeModal(null)}
          onConfirm={async date => {
            await api.setOutcome(outcomeModal.convId, outcomeModal.next, date)
            setOutcomeModal(null)
            await refresh()
          }}
        />
      )}

      {downgradeModal && (
        <ConfirmModal
          message={`当前「${downgradeModal.prevLabel}」，确定没支付吗？`}
          onClose={() => setDowngradeModal(null)}
          onConfirm={async () => {
            await act(api.setOutcome, downgradeModal.convId, 'following')
            setDowngradeModal(null)
          }}
        />
      )}

      {editingReply && (
        <ReplyModal reply={editingReply} ownerId={me.id} onClose={() => setEditingReply(null)}
          onSaved={() => { setEditingReply(null); loadReplies() }} />
      )}
    </div>
  )
}

// 第三栏：这个客户以前每次进线的会话（不含正在看的这通），点开看当时的聊天
function CustomerHistory({ userId, orderId, currentId, agents, media, onViewImage }) {
  const [list, setList] = useState(null)
  const [open, setOpen] = useState({})        // convId -> 消息数组（已展开的）
  const [lost, setLost] = useState({})        // convId -> true：这通历史会话现在没权限看（转出/已删）
  const agentName = id => agents.find(a => a.id === id)?.name || id

  useEffect(() => {
    setList(null); setOpen({}); setLost({})
    api.listCustomerConversations([userId], orderId)
      .then(rows => setList(rows.filter(r => r.id !== currentId)))
      .catch(() => setList([]))
  }, [userId, orderId, currentId])

  async function toggle(id) {
    // 收起：同时清掉上一次的失败提示，下次点开是一次干净的重试
    if (open[id]) return (setOpen(o => { const n = { ...o }; delete n[id]; return n }),
      setLost(l => { const n = { ...l }; delete n[id]; return n }))
    try {
      const msgs = await api.listMessages(id, 500)
      setLost(l => { const n = { ...l }; delete n[id]; return n })
      setOpen(o => ({ ...o, [id]: msgs }))
    } catch (e) {
      // 权限已变（转出/归属解析）或会话已删：说清楚，别装作"没有聊天记录"。
      // 失败态只记在 lost 里、不写 open（写了 open=[id]:[] 会让它变成"已展开"，
      // 再点一下只是收起、要再点一次才会重试）
      setLost(l => ({ ...l, [id]: isConversationLost(e) ? '这通会话已转出或权限已变更，看不了' : e.message }))
    }
  }

  return (
    <>
      <div className="wb-hist-head">历史会话{list ? ` · ${list.length}` : ''}</div>
      <div style={{ flex: 1, overflow: 'auto', padding: '8px 12px 16px' }}>
        {!list && <div className="muted small" style={{ padding: 12 }}>加载中…</div>}
        {list && !list.length && <div className="muted small" style={{ padding: 12 }}>这是该客户第一次进线，没有更早的记录</div>}
        {list?.map(r => (
          <div key={r.id} className="wb-hist-item">
            <button className="wb-hist-row" onClick={() => toggle(r.id)}>
              <span>{fmtTime(r.created_at)}</span>
              <span className="muted small">
                {r.historical_outcome ? `历史标记：${outcomeLabel(r.historical_outcome)}`
                  : outcomeLabel(displayOutcome(r)) || paymentLabel(r) || STATUS_TAG[r.status]?.[1] || r.status} · {r.msg_count} 条
              </span>
              <span className="muted">{open[r.id] ? '▾' : '▸'}</span>
            </button>
            {lost[r.id] && <div className="muted small" style={{ padding: '4px 2px' }}>{lost[r.id]}</div>}
            {open[r.id] && (
              <div style={{ padding: '6px 2px 4px' }}>
                {open[r.id].map(m => {
                  if (m.role === 'system') return <div key={m.id} className="muted small" style={{ textAlign: 'center', margin: '6px 0' }}>{m.content}</div>
                  const mine = m.role !== 'user'
                  const sentMed = findSentMedia(m, media)
                  return (
                    <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', margin: '6px 0' }}>
                      <div style={{ maxWidth: '88%' }}>
                        <div className="muted" style={{ fontSize: 11, margin: '0 6px 2px', textAlign: mine ? 'right' : 'left' }}>
                          {m.role === 'user' ? '客户' : m.role === 'agent' ? agentName(m.agent_id) : 'AI小助'} · {fmtTime(m.created_at)}
                          {m.meta?.recalled_at ? ' · 已撤回' : ''}
                        </div>
                        <div className={`bubble ${m.role === 'user' ? 'them' : m.role === 'agent' ? 'agent' : 'ai'}`}
                          style={{ fontSize: 13, padding: '7px 12px', borderRadius: 16, opacity: m.meta?.recalled_at ? .45 : 1 }}>
                          {m.meta?.media
                            ? <><MediaBubble media={m.meta.media} delivery={m.meta.delivery} onViewImage={onViewImage} />{(m.meta.asr || m.meta.vision || m.role === 'assistant') && <div className="small" style={{ marginTop: 4 }}>{m.meta.vision ? `识图：${m.meta.vision}` : m.content}</div>}</>
                            : sentMed ? <KbMediaBubble med={sentMed} onViewImage={onViewImage} />
                            : m.content}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  )
}

// 第三栏顶部：每个运营自己的宠物。第一次来先开「新手盲盒」（6 选 1，结果服务端随机），
// 之后平时呼吸浮动、点一下蹦一下，状态跟着侧栏的接单开关
// 图片地址带版本号：换了同名图片要把 ASSET_V 加 1，不然浏览器一直用缓存的旧图（2026-09-29 项链踩过）
const ASSET_V = 3
const petImg = id => `${import.meta.env.BASE_URL}pets/${id}.png?v=${ASSET_V}`
const petAsset = (name, ext = 'png') => `${import.meta.env.BASE_URL}pets/${name}.${ext}?v=${ASSET_V}`
// 头像框（Gavin 给的透明图）、装扮样品，现在人人可试穿。编号要和后端 PET_ITEMS 一致。
// hole = 中间圆孔直径占图宽的比例（按图实测），用来把框放大到圆孔刚好套住宠物圆圈
const FRAMES = [
  { id: 'pink', name: '粉绒心语', hole: 0.541 }, { id: 'gold', name: '金色梦境', hole: 0.581 },
  { id: 'cyber', name: '赛博星环', hole: 0.541 }, { id: 'mint', name: '薄荷森林', hole: 0.497 },
]
// 配饰（Gavin 给的透明图，可以多个一起戴）。w = 宽度占圆圈直径的比例，top = 顶边位置（占直径比例）。
// 宠物体型差很多（云朵没脖子、雪兔脖子高），按宠物单独给位置
// per: [宽, 顶, 水平中心(默认 0.5), 旋转角度, 换用的图]，都是占圆圈直径的比例，6 只都合成预览调过。
// 皇冠压在头顶；项链挂在头身交界（云朵没脖子，用更短的一截放在嘴巴下面）；眼镜盖住眼睛；蝴蝶结别在头顶右侧
const ACCS = [
  { id: 'crown', name: '星星皇冠', w: 0.44, top: -0.08, z: 5, per: {
    horn: [0.44, -0.1], bunny: [0.36, -0.12], peach: [0.44, -0.08], sprout: [0.42, -0.08], moon: [0.44, -0.08], cloud: [0.4, -0.06],
  } },
  { id: 'necklace', name: '金链项链', w: 0.42, top: 0.55, z: 4, per: {
    horn: [0.42, 0.55], bunny: [0.34, 0.6], peach: [0.4, 0.55], sprout: [0.42, 0.56], moon: [0.44, 0.55],
    cloud: [0.44, 0.6, 0.5, 0, 'acc-necklace-short'],
  } },
  { id: 'glasses', name: '星空墨镜', w: 0.44, top: 0.28, z: 6, per: {
    horn: [0.5, 0.29], bunny: [0.36, 0.39], peach: [0.42, 0.26], sprout: [0.44, 0.27], moon: [0.46, 0.3], cloud: [0.48, 0.36],
  } },
  { id: 'bow', name: '波点蝴蝶结', w: 0.26, top: 0.1, z: 7, per: {
    horn: [0.26, 0.08, 0.68, 20], bunny: [0.22, 0.2, 0.64, 20], peach: [0.26, 0.06, 0.66, 20],
    sprout: [0.26, 0.1, 0.68, 20], moon: [0.26, 0.1, 0.68, 20], cloud: [0.26, 0.1, 0.66, 20],
  } },
]

function PetAvatar({ pet, equip = {}, size = 124, live, hop, onImgClick, onAnimationEnd, children }) {
  const accs = ACCS.filter(a => (equip.accs || []).includes(a.id))
  const frame = FRAMES.find(f => f.id === equip.frame)
  // 框放大到圆孔比宠物圆圈略小一点（0.97），框的内沿正好压住圆圈边
  const fs = frame ? Math.round(size / frame.hole * 0.97) : size
  return (
    <div className={`pet-pic rar-${pet.rarity}`}
      style={{ width: size, height: size, '--s': `${size}px`, margin: frame ? `${(fs - size) / 2}px` : undefined }}>
      {/* 只有宠物本体（连同身上的配饰）呼吸、蹦跳；圆圈、头像框、仓库按钮不动，按钮才好点 */}
      <div className={`pet-body${live ? ' pet-live' : ''}${hop ? ' hop' : ''}`} onAnimationEnd={onAnimationEnd}>
        <img className="pet-main" src={petImg(pet.id)} alt={pet.name} onClick={onImgClick} />
        {accs.map(a => {
          const [w, top, left = 0.5, rot = 0, img] = a.per?.[pet.id] || [a.w, a.top]
          return <img key={a.id} className="pet-acc" src={petAsset(img || `acc-${a.id}`)} alt=""
            style={{ width: size * w, top: size * top, left: size * left, zIndex: a.z, transform: `translateX(-50%) rotate(${rot}deg)` }} />
        })}
      </div>
      {frame && <img className="pf-img" src={petAsset(`frame-${frame.id}`, 'png')} alt=""
        style={{ width: fs, height: fs, left: (size - fs) / 2 - 3, top: (size - fs) / 2 - 3 }} />}
      {children}
    </div>
  )
}
function Pet({ duty }) {
  const [data, setData] = useState(null)       // { pet_name, pet, rarities }
  const [editing, setEditing] = useState(false)
  const [hop, setHop] = useState(false)
  useEffect(() => { api.getMyPet().then(setData).catch(() => setData({})) }, [])
  const working = duty?.online && duty?.accepting
  if (!data) return <div className="pet" />
  if (!data.pet) return <BlindBox onGot={setData} />
  const { pet } = data
  return (
    <div className="pet">
      <PetAvatar pet={pet} equip={data.equip} size={92} live hop={hop} onImgClick={() => setHop(true)} onAnimationEnd={() => setHop(false)} />
      {/* 仓库按钮放名字旁边：放圆圈上会挡住宠物，戴大头像框时还会被框盖住 */}
      <div className="pet-name row" style={{ gap: 6, justifyContent: 'center' }}>
        {data.pet_name || pet.name}
        <button className="pet-edit" title="我的仓库" onClick={() => setEditing(true)}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
        </button>
      </div>
      <div className="pet-state">
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: working ? 'var(--ok)' : '#9ca3af' }} />
        {working ? '正在接单' : '已挂起'}
      </div>
      {editing && <PetStore data={data} onClose={() => setEditing(false)}
        onSaved={d => { setData(d); setEditing(false) }} />}
    </div>
  )
}

// 6 个盒子：同一张猫爪盒换色调——粉、薄荷、蜜桃 / 天蓝、薰衣草、青柠
const BOXES = [['paw', 0], ['paw', 150], ['paw', 40], ['paw', 190], ['paw', 260], ['paw', 90]]

// 开局开箱：大宝箱「开局开箱」→ 点开换成打开的宝箱 → 弹窗 6 个盲盒选 1 → 盒子晃 → 宠物弹出
function BlindBox({ onGot }) {
  const [opened, setOpened] = useState(false)
  const [showBoxes, setShowBoxes] = useState(false)
  const [picked, setPicked] = useState(null)
  const [got, setGot] = useState(null)
  const [err, setErr] = useState('')
  function openChest() {
    if (opened) return setShowBoxes(true)
    setOpened(true)
    setTimeout(() => setShowBoxes(true), 700)   // 先让人看清宝箱打开了，再弹盲盒
  }
  async function pick(i) {
    setPicked(i); setErr('')
    // 盒子至少晃 1.2 秒再揭晓，太快没有开盒的感觉
    const [res] = await Promise.all([api.drawNewbiePet().catch(e => ({ error: e.message })), new Promise(r => setTimeout(r, 1200))])
    if (res.error) { setErr(res.error); setPicked(null); return }
    setGot(res)
  }
  return (
    <div className="chest">
      <div className="chest-title">开局开箱</div>
      <img src={petAsset(opened ? 'chest-open' : 'chest-closed')} alt="宝箱"
        className={opened ? 'opened' : 'idle'} onClick={openChest} />
      <div className="small muted" style={{ marginTop: 6 }}>{opened ? '选一个盲盒，领养你的第一只宠物' : '点击宝箱'}</div>
      {showBoxes && (
        <div className="modal-bg">
          <div className="modal box-modal">
            {got ? (
              <>
                <div className={`pet-pic pet-pop rar-${got.pet.rarity}`} style={{ margin: '8px auto 0' }}>
                  <img className="pet-main" src={petImg(got.pet.id)} alt={got.pet.name} />
                </div>
                <div className="pet-name">恭喜获得「{got.pet.name}」</div>
                <span className={`rar-tag rar-${got.pet.rarity}`}>{got.rarities?.[got.pet.rarity]}</span>
                <div><button className="btn btn-primary" style={{ marginTop: 18 }} onClick={() => onGot(got)}>带它回家</button></div>
              </>
            ) : (
              <>
                <h2 style={{ marginBottom: 4 }}>选一个盲盒</h2>
                <div className="small muted">每人只有一次机会</div>
                <div className="box-grid">
                  {BOXES.map(([kind, hue], i) => (
                    <button key={i} className={`box ${kind}${picked === i ? ' picked' : ''}`}
                      disabled={picked !== null && picked !== i}
                      style={{ filter: `hue-rotate(${hue}deg) drop-shadow(0 4px 8px rgba(0,0,0,.25))` }} onClick={() => picked === null && pick(i)} />
                  ))}
                </div>
                {err && <div className="small" style={{ color: 'var(--danger)' }}>{err}</div>}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// 我的仓库：名字可改；头像框、装扮点一下就在上面预览，保存后第三栏跟着换
function PetStore({ data, onClose, onSaved }) {
  const { pet, rarities } = data
  const [name, setName] = useState(data.pet_name || '')
  const [equip, setEquip] = useState({ frame: data.equip?.frame || null, accs: data.equip?.accs || [] })
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  // 头像框只能一个，再点一次卸下；配饰可多选，点一下戴上/卸下
  const toggleFrame = id => setEquip(e => ({ ...e, frame: e.frame === id ? null : id }))
  const toggleAcc = id => setEquip(e => ({ ...e, accs: e.accs.includes(id) ? e.accs.filter(x => x !== id) : [...e.accs, id] }))
  async function save() {
    setBusy(true); setErr('')
    try { onSaved(await api.updateMyPet({ pet_name: name.trim(), equip })) }
    catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <h2>我的仓库</h2>
        <div className="row" style={{ gap: 22, marginBottom: 18 }}>
          <PetAvatar pet={pet} equip={equip} size={132} live />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{pet.name} <span className={`rar-tag rar-${pet.rarity}`}>{rarities?.[pet.rarity]}</span></div>
            <label className="small muted">名字</label>
            <input className="input" value={name} maxLength={12} onChange={e => setName(e.target.value)} placeholder={pet.name} style={{ marginTop: 4 }} />
          </div>
        </div>
        <div className="field">
          <label>头像框 <span className="small muted">点一下试戴，再点卸下</span></label>
          <div className="slot-grid">
            {FRAMES.map(f => (
              <button key={f.id} className={`slot pick${equip.frame === f.id ? ' on' : ''}`} onClick={() => toggleFrame(f.id)} title={f.name}>
                <img src={petAsset(`frame-${f.id}`, 'png')} alt="" style={{ width: 44, height: 44 }} />
                <em>{f.name}</em>
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <label>配饰 <span className="small muted">可以多个一起戴</span></label>
          <div className="slot-grid">
            {ACCS.map(a => (
              <button key={a.id} className={`slot pick${equip.accs.includes(a.id) ? ' on' : ''}`} onClick={() => toggleAcc(a.id)} title={a.name}>
                <img src={petAsset(`acc-${a.id}`)} alt="" style={{ width: 40, height: 40, objectFit: 'contain' }} /><em>{a.name}</em>
              </button>
            ))}
          </div>
          <div className="small muted" style={{ marginTop: 6 }}>现在是样品，人人可试穿；以后改成积分抽到才能用</div>
        </div>
        <div className="err">{err}</div>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn" onClick={onClose}>取消</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? '保存中…' : '保存'}</button>
        </div>
      </div>
    </div>
  )
}

// 派单工具条。派单改的是「客户归属」，不动会话状态——AI 该接待照常接待，
// 只是这个客户之后归谁跟进定死了，其他运营在自己的列表里看不到。

function AssignBar({ convs, agents, picked, setPicked, onDone }) {
  // 派给谁：可以点选多个人，选 1 个就直接分给他，选多个就在这几个人里平均分（2026-09-30 Gavin 要求；原来只能选 1 个或全部平分）
  const [to, setTo] = useState([])
  const [busy, setBusy] = useState(false)
  // 只列在班的：关了接单开关/停用的人不该再出现在分配目标里，避免转过去没人跟进
  const online = agents.filter(a => a.role !== 'admin' && a.online && a.accepting !== false && a.active !== false)
  const n = picked.size
  const allPicked = convs.length > 0 && convs.every(c => picked.has(c.id))
  // 能选的人不超过勾选的单数：1 单只能给 1 个人，分给 2 个人没意义（2026-09-30 Gavin 指出）。
  // 勾选变少时，已选的人跟着减
  useEffect(() => { if (to.length > Math.max(n, 1)) setTo(v => v.slice(0, Math.max(n, 1))) }, [n])
  const pickAgent = id => setTo(v => {
    if (v.includes(id)) return v.filter(x => x !== id)
    if (n <= 1) return [id]                                // 只有 1 单：点谁就换成谁
    return v.length >= n ? [...v.slice(1), id] : [...v, id] // 选满了：顶掉最早选的
  })

  // fn(force)：选中的有已支付客户、会改掉归属时，后端先拒绝并列出来；管理员确认（归属有异议）后带 force 重发
  async function run(fn, label) {
    setBusy(true)
    try {
      let r
      try { r = await fn(false) } catch (e) {
        if (!e.message.startsWith('以下客户已支付')) throw e
        if (!(await confirmBox(`${e.message}\n\n只在归属有异议时才改派。确定改派？`))) return onDone('已取消，没有改派')
        r = await fn(true)
      }
      onDone(r?.detail?.length ? `${label}：${r.detail.join('、')}` : `${label}：${r?.assigned ?? 0} 个客户`)
    } catch (e) { onDone(e.message) } finally { setBusy(false) }
  }

  const ids = () => [...picked]

  return (
    <div style={{ margin: '0 12px 6px', padding: '6px 14px', borderRadius: picked.size ? 18 : 999, background: 'var(--hover)' }}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <label className="row small" style={{ gap: 5, cursor: 'pointer' }}>
          <input type="checkbox" checked={allPicked}
            onChange={e => setPicked(e.target.checked ? new Set(convs.map(c => c.id)) : new Set())} />
          全选
        </label>
        <span className="small muted">{n ? `已选 ${n} 个` : '勾选后可派单'}</span>
      </div>

      {n > 0 && (
        <div className="row" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          <span className="small muted">分给：</span>
          {!online.length && <span className="small muted">当前没有在班的运营</span>}
          {online.map(a => {
            const on = to.includes(a.id)
            return (
              <button key={a.id} className="btn btn-sm"
                style={on ? { background: 'var(--brand)', borderColor: 'var(--brand)', color: '#fff' } : {}}
                onClick={() => pickAgent(a.id)}>
                {a.name || a.id}
              </button>
            )
          })}
          <button className="btn btn-sm btn-primary" disabled={!to.length || busy}
            onClick={() => {
              const live = to.filter(id => online.some(a => a.id === id))   // 选完之后有人下线了，不能再分给他
              run(force => live.length === 1 ? api.assignConvs(ids(), live[0], force) : api.distributeConvs(ids(), live, force),
                live.length === 1 ? '已分配' : '已平均分配')
            }}>
            {to.length > 1 ? `平均分给 ${to.length} 人`
              : to.length === 1 ? `分给 ${online.find(a => a.id === to[0])?.name || to[0]}` : '先点选运营'}
          </button>
        </div>
      )}
    </div>
  )
}

// 标成交/失败前选日期：忘了当天标、过几天才想起来补标的，可以选回真正发生那天，
// 统计就按那天算，不是按「点标签这一刻」算（2026-10-01 Gavin 定）
// 通用小确认框：跟 OutcomeDateModal 同样的尺寸和按钮布局（确认在左、取消在右），
// 没有日期选择，纯粹是「确定要这么改吗」
function ConfirmModal({ message, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  async function save() {
    setBusy(true); setErr('')
    try { await onConfirm() } catch (e) { setErr(e.message); setBusy(false) }
  }
  return (
    <div className="modal-bg" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 340, padding: 20 }}>
        <p style={{ textAlign: 'center' }}>{message}</p>
        {err && <div className="err">{err}</div>}
        <div className="row" style={{ justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button className="btn btn-primary" onClick={save} disabled={busy}>确认</button>
          <button className="btn" onClick={onClose} disabled={busy}>取消</button>
        </div>
      </div>
    </div>
  )
}

function OutcomeDateModal({ prevLabel, nextLabel, isFailed, isCredit, onClose, onConfirm }) {
  const today = beijingDateString()
  const [date, setDate] = useState(today)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const dateRef = useRef(null)

  const dateLabel = isCredit ? '实际办理日期' : isFailed ? '实际失败日期' : '实际支付日期'
  const confirmLine = prevLabel
    ? `确认从「${prevLabel}」改为「${nextLabel}」吗？`
    : (isCredit ? '确认记录在这个日期办理信用卡吗？' : isFailed ? '确认记录在这个日期失败的吗？' : '确认记录在这个日期成交的吗？')

  async function save() {
    setBusy(true); setErr('')
    try { await onConfirm(date) } catch (e) { setErr(e.message); setBusy(false) }
  }

  return (
    <div className="modal-bg" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 340, padding: 20 }}>
        <h2 style={{ textAlign: 'center' }}>标记「{nextLabel}」</h2>
        {prevLabel && <p className="small muted">当前已标记为「{prevLabel}」，改成「{nextLabel}」</p>}
        <div className="field">
          <label>{dateLabel}</label>
          <input ref={dateRef} className="input" type="date" value={date} max={today}
            onChange={e => setDate(e.target.value)}
            onClick={() => dateRef.current?.showPicker?.()} />
          <p className="small muted" style={{ marginTop: 4 }}>{confirmLine}</p>
        </div>
        {err && <div className="err">{err}</div>}
        <div className="row" style={{ justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button className="btn btn-primary" onClick={save} disabled={busy}>确认</button>
          <button className="btn" onClick={onClose} disabled={busy}>取消</button>
        </div>
      </div>
    </div>
  )
}

function RenameModal({ conv, onClose, onSaved }) {
  const [name, setName] = useState(conv.nickname || '')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  async function save() {
    if (!name.trim()) return setErr('名称不能为空')
    setBusy(true)
    try { await api.renameCustomer(conv.user_id, name.trim()); onSaved() }
    catch (e) { setErr(e.message); setBusy(false) }
  }

  async function reset() {
    setBusy(true)
    try { await api.resetCustomerName(conv.user_id); onSaved() }
    catch (e) { setErr(e.message); setBusy(false) }
  }

  return (
    <div className="modal-bg" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h2>修改客户名称</h2>
        <div className="field">
          <label>显示名称</label>
          <input className="input" value={name} autoFocus
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && save()}
            placeholder="车牌号 / 手机号 / 客户姓名" />
          <div className="small muted" style={{ marginTop: 6 }}>
            手动改名后，客户再发新车牌也不会自动覆盖。点「恢复自动识别」可解除。
          </div>
        </div>
        <div className="err">{err}</div>
        <div className="spread" style={{ marginTop: 8 }}>
          <button className="btn btn-sm" onClick={reset} disabled={busy}>恢复自动识别</button>
          <div className="row">
            <button className="btn" onClick={onClose}>取消</button>
            <button className="btn btn-primary" onClick={save} disabled={busy}>保存</button>
          </div>
        </div>
      </div>
    </div>
  )
}


// 管理员改客户归属：不要求接收人在线，锁定的也能改；锁定状态保留（锁给新归属人）
function OwnerModal({ conv, agents, onClose, onSaved }) {
  const [to, setTo] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const list = agents.filter(a => a.active !== false && a.id !== conv.owner_agent_id)

  async function submit() {
    if (!to) return setErr('请选择新的归属人')
    setBusy(true)
    try { await api.changeOwner(conv.user_id, to, conv.id); onSaved() }
    catch (e) { setErr(e.message); setBusy(false) }
  }

  return (
    <div className="modal-bg" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h2>改归属</h2>
        <div className="small muted" style={{ marginBottom: 14 }}>
          客户：{conv.nickname || conv.user_id?.replace('wecom:', '')} · 当前归属 {conv.owner_name || conv.owner_agent_id || '无'}
          {conv.owner_locked && '（已锁定）'}
        </div>
        <div className="field">
          <label>改给</label>
          <select className="input" value={to} onChange={e => setTo(e.target.value)}>
            <option value="">请选择…</option>
            {list.map(a => (
              <option key={a.id} value={a.id}>{a.name || a.id}{a.online ? '' : '（离线）'}</option>
            ))}
          </select>
        </div>
        {!conv.order_sync_enabled && <div className="small muted">只改这个客户算谁的，不改正在接待的人。改完归属就固定了：归属人离线时在班的人可以帮忙处理，但不会自动改派，别人接管、回复、成交也不会改归属。只有管理员能操作。</div>}
        <div className="err">{err}</div>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn" onClick={onClose}>取消</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy || !to}>确认修改</button>
        </div>
      </div>
    </div>
  )
}

function TransferModal({ conv, agents, me, onClose, onSaved }) {
  const [to, setTo] = useState('')
  const [note, setNote] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  // 只列在班的：转给离线/关了接单/停用的人等于把客户晾在那。online 由后端按 150 秒心跳判活，不是数据库里的标志位
  const others = agents.filter(a => a.role !== 'admin' && a.online && a.accepting !== false && a.active !== false
    && a.id !== me.id && a.id !== conv.assigned_agent_id)

  async function submit() {
    if (!to) return setErr('请选择接收客服')
    setBusy(true)
    try { await api.transfer(conv.id, to, note.trim()); onSaved() }
    catch (e) { setErr(e.message); setBusy(false) }
  }

  return (
    <div className="modal-bg" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h2>转接会话</h2>
        <div className="small muted" style={{ marginBottom: 14 }}>
          客户：{conv.nickname || conv.user_id?.replace('wecom:', '')}
          {conv.owner_agent_id && <> · 当前归属 {conv.owner_name || conv.owner_agent_id}</>}
        </div>
        <div className="field">
          <label>转给</label>
          <select className="input" value={to} onChange={e => setTo(e.target.value)}>
            <option value="">请选择客服…</option>
            {others.map(a => (
              <option key={a.id} value={a.id}>{a.name || a.id}</option>
            ))}
          </select>
          {others.length === 0 && (
            <div className="small muted" style={{ marginTop: 6 }}>当前没有其他客服在线，无法转接</div>
          )}
        </div>
        <div className="field">
          <label>转接备注（可选）</label>
          <input className="input" value={note} onChange={e => setNote(e.target.value)}
            placeholder="如：非本人客户，转原跟单客服" />
        </div>
        <div className="small muted">转接后该客户归属将变更为接收人。</div>
        <div className="err">{err}</div>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn" onClick={onClose}>取消</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy || !to}>确认转接</button>
        </div>
      </div>
    </div>
  )
}
